/**
 * ANANT VYAPAR — CRM & Mini-ERP for local shopkeepers.
 * Phase 1 MVP: workspace init, customer CRM, billing, ledger, magic QR, plans, exports.
 * All exports client-side (jsPDF, SheetJS) → zero server load.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Store, Users, FileText, Wallet, Sparkles, QrCode, Crown, Award, Download,
  Plus, Trash2, Edit3, Search, AlertTriangle, Share2, ChevronRight, X, CheckCircle2,
  TrendingUp, Clock, Shield, Package,
} from 'lucide-react';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';
import VoiceInputHelper, { parseNumericFields } from '../../components/VoiceInputHelper';
import BulkMessenger from '../../components/BulkMessenger';
import ShareButton from '../../components/ShareButton';
import AccountingTab from './AccountingTab';
import { VyaparLangToggle, useVyaparI18n } from './VyaparI18n';
import { useAuth } from '../../context/AuthContext';

// ============ Root component with tab routing ============
export default function AnantVyapar() {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [showInit, setShowInit] = useState(false);
  const [showCreateAdditional, setShowCreateAdditional] = useState(false);
  const navigate = useNavigate();

  const loadWorkspace = useCallback(async (preferId) => {
    try {
      const r = await api.get('/vyapar/workspace/me');
      const list = r.data.workspaces || [];
      setWorkspaces(list);
      setPlan(r.data.plan);
      if (list.length === 0) {
        setShowInit(true);
      } else {
        // pick preferred (just created) or currently active or first
        const pick = list.find((w) => w.id === preferId) || list.find((w) => w.id === workspace?.id) || list[0];
        setWorkspace(pick);
      }
    } catch (e) {
      setShowInit(true);
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  if (loading) return <div className="min-h-screen pt-24 text-white text-center">Loading Anant Smart Vyapar System…</div>;

  if (showInit || !workspace) {
    return <WorkspaceInitScreen onCreated={(ws, pl) => { setWorkspace(ws); setPlan(pl); setShowInit(false); loadWorkspace(ws.id); }} />;
  }

  const canCreateMore = (plan?.max_workspaces || 1) > workspaces.length;

  return (
    <div className="min-h-screen pt-20 pb-32 px-4 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white" data-testid="vyapar-root">
      <div className="max-w-6xl mx-auto">
        <VyaparHeader
          workspace={workspace}
          workspaces={workspaces}
          plan={plan}
          onSwitch={(ws) => setWorkspace(ws)}
          onCreateNew={() => setShowCreateAdditional(true)}
          canCreateMore={canCreateMore}
          onRefresh={loadWorkspace}
        />
        <TabBar tab={tab} setTab={setTab} plan={plan} />
        {tab === 'dashboard' && <DashboardTab workspace={workspace} plan={plan} onSwitchTab={setTab} onRefresh={loadWorkspace} />}
        {tab === 'customers' && <CustomersTab workspace={workspace} />}
        {tab === 'billing' && <BillingTab workspace={workspace} />}
        {tab === 'ledger' && <LedgerTab workspace={workspace} />}
        {tab === 'accounting' && <AccountingTab workspace={workspace} />}
        {tab === 'inventory' && <InventoryTab workspace={workspace} />}
        {tab === 'qr' && <MagicQRTab workspace={workspace} />}
        {tab === 'setup' && <SetupTab workspace={workspace} onRefresh={loadWorkspace} />}
        {tab === 'plans' && <PlansTab plan={plan} onPurchased={loadWorkspace} />}
      </div>
      {showCreateAdditional && (
        <AddWorkspaceModal
          plan={plan}
          onClose={() => setShowCreateAdditional(false)}
          onCreated={(newWs) => { setShowCreateAdditional(false); loadWorkspace(newWs.id); }}
        />
      )}
    </div>
  );
}

// ============ Header + Plan strip + Workspace Switcher ============
const VyaparHeader = ({ workspace, workspaces = [], plan, onSwitch, onCreateNew, canCreateMore }) => {
  const now = Date.now();
  const expIso = plan?.expires_at;
  let cd = '';
  if (expIso) {
    const diff = Math.max(0, new Date(expIso).getTime() - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    cd = `${d}d ${h}h ${m}m`;
  }
  const isExpired = !plan?.active;
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-fuchsia-400 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">
            <Store className="w-4 h-4" /> Anant Smart Vyapar System · ERP · CRM · Accounting
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-pink-300 to-amber-300 bg-clip-text text-transparent mt-1 truncate">
            {workspace.business_name}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {workspace.industry} · A complete business management service for any type of business & service providers
          </p>
          {/* Workspace switcher — only shown when user has >1 or can create more */}
          {(workspaces.length > 1 || canCreateMore) && (
            <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="vyapar-ws-switcher">
              <span className="text-[10px] uppercase font-bold text-slate-400">Business:</span>
              {workspaces.map((w) => (
                <button
                  key={w.id}
                  onClick={() => onSwitch(w)}
                  data-testid={`vyapar-ws-${w.id}`}
                  className={`text-[11px] px-2 py-1 rounded-lg border transition ${w.id === workspace.id ? 'border-fuchsia-400 bg-fuchsia-500/20 text-fuchsia-100' : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'}`}
                  title={w.industry}
                >
                  {w.id === workspace.id && '● '}{w.business_name}
                </button>
              ))}
              {canCreateMore && (
                <button
                  onClick={onCreateNew}
                  data-testid="vyapar-ws-add"
                  className="text-[11px] px-2 py-1 rounded-lg border border-dashed border-emerald-400/50 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 font-bold"
                >
                  + Add New ({(plan?.max_workspaces || 1) - workspaces.length} slot left)
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-2">
          <ShareButton type="page" itemName="Anant Smart Vyapar System" variant="icon" className="scale-110" />
          <VyaparLangToggle />
          <a
            href={(() => {
              const link = `${window.location.origin}/vyapar`;
              const msg = `🏪 Anant Smart Vyapar System try karo!\n\nA complete business management service for any type of business & service providers — CRM · Billing · Khata Bahi · **Full ERP Accounting** (Chart of Accounts, Vouchers, Trial Balance, P&L, Balance Sheet) · Stock · Magic QR. AI-powered!\n\n${link}`;
              return `https://wa.me/?text=${encodeURIComponent(msg)}`;
            })()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:scale-105 transition shadow-lg shadow-green-500/30"
            data-testid="vyapar-viral-share-wa"
            title="Share on WhatsApp"
          >
            ✨ Share & Earn
          </a>
        </div>
        <ATWalletBadge compact />
        <div className={`rounded-xl border px-3 py-2 text-xs ${plan?.is_trial ? 'border-emerald-400/40 bg-emerald-500/10' : plan?.active ? 'border-amber-400/40 bg-amber-500/10' : 'border-red-400/40 bg-red-500/10'}`} data-testid="vyapar-plan-strip">
          <div className="font-bold">{plan?.plan_name || 'No active plan'}</div>
          {plan?.active && <div className="text-slate-300 mt-0.5">Expires in <span className="font-mono text-emerald-300">{cd}</span></div>}
          {isExpired && <div className="text-red-300 mt-0.5">Please renew to continue</div>}
          {plan?.max_workspaces > 1 && <div className="text-[10px] text-slate-400 mt-0.5">Slots: {workspaces.length}/{plan.max_workspaces}</div>}
        </div>
        </div>
      </div>
    </div>
  );
};

// ============ Add Additional Workspace Modal (VIP feature) ============
const AddWorkspaceModal = ({ plan, onClose, onCreated }) => {
  const [industries, setIndustries] = useState([]);
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('general');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/vyapar/plans').then((r) => setIndustries(r.data.industries || [])); }, []);
  const submit = async () => {
    if (!name.trim()) return toast.error('Business ka naam daalein');
    setBusy(true);
    try {
      const r = await api.post('/vyapar/workspace/create', { business_name: name.trim(), industry });
      toast.success(`✅ New workspace "${name}" ban gaya`);
      onCreated(r.data.workspace);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="vyapar-add-ws-modal">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-bold">➕ New Business / Category</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-2 mb-3">
          VIP plan me aap {plan?.max_workspaces || 3} tak alag business/categories ka database rakh sakte hain (ek hi login me).
        </div>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-400">Business Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Anil Medical Store" data-testid="add-ws-name" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-400">Industry / Category</label>
            <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="add-ws-industry" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2">
              {industries.map((i) => <option key={i.id} value={i.id} className="bg-slate-900">{i.icon || '🏪'} {i.label}</option>)}
            </select>
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="add-ws-submit" className="w-full mt-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold text-sm disabled:opacity-60">
          {busy ? 'Creating…' : 'Create Workspace'}
        </button>
      </div>
    </div>
  );
};

// ============ Tab Bar ============
const TABS = [
  { id: 'dashboard',  labelKey: 'home',        label: 'Home',      icon: Store },
  { id: 'customers',  labelKey: 'customers',   label: 'Customers', icon: Users },
  { id: 'billing',    labelKey: 'billing',     label: 'Billing',   icon: FileText },
  { id: 'ledger',     labelKey: 'udhari',      label: 'Udhari',    icon: Wallet },
  { id: 'accounting', labelKey: 'accounting',  label: 'Accounts',  icon: FileText },
  { id: 'categories', labelKey: 'category',    label: 'Categories', icon: Package },
  { id: 'inventory',  labelKey: 'inventory',   label: 'Stock',     icon: Package },
  { id: 'qr',         labelKey: 'magic_qr',    label: 'Magic QR',  icon: QrCode },
  { id: 'setup',      labelKey: 'setup',       label: 'Setup',     icon: Package },
  { id: 'plans',      labelKey: 'plans',       label: 'Plans',     icon: Crown },
];
const TabBar = ({ tab, setTab }) => {
  const { t } = useVyaparI18n();
  return (
    <div className="flex gap-1.5 overflow-x-auto mb-4 pb-1" data-testid="vyapar-tabs">
      {TABS.map((tt) => (
        <button
          key={tt.id}
          onClick={() => setTab(tt.id)}
          data-testid={`vyapar-tab-${tt.id}`}
          className={`shrink-0 px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition ${tab === tt.id ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-lg' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
        >
          <tt.icon className="w-3.5 h-3.5" />
          {t(tt.labelKey) || tt.label}
        </button>
      ))}
    </div>
  );
};

// ============ Workspace Init (First-time setup) ============
const WorkspaceInitScreen = ({ onCreated }) => {
  const [industries, setIndustries] = useState([]);
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('kirana');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/vyapar/plans').then((r) => setIndustries(r.data.industries || [])); }, []);
  const submit = async () => {
    if (!name.trim()) return toast.error('Business ka naam daalein');
    setBusy(true);
    try {
      const r = await api.post('/vyapar/workspace/init', { business_name: name.trim(), industry });
      toast.success(r.data.already ? 'Welcome back!' : '🎉 7-Day free trial activated!');
      onCreated(r.data.workspace, r.data.plan);
    } catch (e) { toast.error(e.response?.data?.detail || 'Setup failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white flex items-center justify-center" data-testid="vyapar-init">
      <div className="w-full max-w-lg rounded-2xl border border-fuchsia-400/30 bg-slate-900/80 p-6">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-pink-500 items-center justify-center mb-3">
            <Store className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-extrabold">Welcome to Anant Smart Vyapar System</h2>
          <p className="text-slate-400 text-sm mt-1">Apni dukan ya business ka detail daalein — 7 din free trial start hoga</p>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-300">Business Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="vyapar-init-name" placeholder="e.g. Sharma Kirana Store" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-300">Industry</label>
            <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="vyapar-init-industry" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
              {industries.map((i) => <option key={i.id} value={i.id} className="bg-slate-900">{i.label}</option>)}
            </select>
          </div>
          <button onClick={submit} disabled={busy} data-testid="vyapar-init-submit" className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold disabled:opacity-60">
            {busy ? 'Starting…' : '🎁 Start 7-Day Free Trial'}
          </button>
          <div className="text-center text-[11px] text-emerald-300 mt-2">
            ✓ No AT deducted · ✓ Full features · ✓ Upgrade anytime
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Dashboard Tab (overview) ============
const DashboardTab = ({ workspace, plan, onSwitchTab }) => {
  const [stats, setStats] = useState({ customers: 0, invoices: 0, total_due: 0, defaulters: 0 });
  const [events, setEvents] = useState({ birthdays: [], anniversaries: [] });
  useEffect(() => {
    (async () => {
      try {
        const [cs, invs, led, ev] = await Promise.all([
          api.get(`/vyapar/customers?workspace_id=${workspace.id}&limit=1`),
          api.get(`/vyapar/invoices?workspace_id=${workspace.id}&limit=1`),
          api.get(`/vyapar/ledger/summary?workspace_id=${workspace.id}`),
          api.get(`/vyapar/events/upcoming?workspace_id=${workspace.id}&days=30`),
        ]);
        const cs2 = await api.get(`/vyapar/customers?workspace_id=${workspace.id}&tag=defaulter&limit=200`);
        setStats({
          customers: cs.data.count,
          invoices: invs.data.invoices.length,
          total_due: led.data.total_due || 0,
          defaulters: cs2.data.count || 0,
        });
        setEvents({ birthdays: ev.data.birthdays || [], anniversaries: ev.data.anniversaries || [] });
      } catch {}
    })();
  }, [workspace.id]);
  const sendWish = (row, type) => {
    const num = (row.whatsapp || row.mobile || '').replace(/\D/g, '');
    const msg = type === 'birthday'
      ? `🎂 Janmdin ki hardik shubhkamnayein ${row.name}! ${workspace.business_name} ki taraf se aapko dher saara pyar. Ek special discount aapke intezar mein hai. 🎁`
      : `💐 Shubh salgirah ${row.name}! ${workspace.business_name} ki taraf se aapke saath ki yeh khubsurat yatra ke liye dhanyawad. Ek special anniversary offer aapke naam! 🎁`;
    window.open(`https://wa.me/${num.length === 10 ? '91' + num : num}?text=${encodeURIComponent(msg)}`, '_blank');
  };
  return (
    <div className="space-y-4" data-testid="vyapar-dashboard-tab">
      {/* Daily Galla WhatsApp Share + Team Bulk */}
      {(() => {
        const teamKey = `vyapar_team_wa_${workspace.id}`;
        const getTeam = () => (localStorage.getItem(teamKey) || '').split(',').map((s) => s.trim().replace(/\D/g, '')).filter((n) => n.length >= 10);
        const buildDailyMsg = async () => {
          const today = new Date();
          const fromISO = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
          const toISO = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();
          const r = await api.get(`/vyapar/invoices?workspace_id=${workspace.id}&from=${fromISO}&to=${toISO}&limit=500`);
          const invs = r.data.invoices || [];
          const totalSale = invs.reduce((s, i) => s + Number(i.total || 0), 0);
          const cashIn = invs.reduce((s, i) => s + Number(i.paid || 0), 0);
          const pendingToday = invs.reduce((s, i) => s + Number(i.due || 0), 0);
          const dateStr = today.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
          return `📊 *${workspace.business_name}* — Daily Galla\n📅 ${dateStr}\n\n` +
            `🧾 Bills Today: *${invs.length}*\n💰 Total Sale: *₹${totalSale.toLocaleString('en-IN')}*\n` +
            `✅ Cash In: *₹${cashIn.toLocaleString('en-IN')}*\n⏳ Pending (today): *₹${pendingToday.toLocaleString('en-IN')}*\n\n` +
            `📈 Overall:\n👥 Customers: *${stats.customers}*\n💸 Total Udhari: *₹${Number(stats.total_due).toLocaleString('en-IN')}*\n⚠️ Defaulters: *${stats.defaulters}*\n\n` +
            `_Sent via Anant Smart Vyapar_ 🙏`;
        };
        const editTeam = () => {
          const cur = localStorage.getItem(teamKey) || '';
          const v = window.prompt('Team WhatsApp numbers (comma-separated, 10-digit each):\n\nExample: 9876543210, 9123456789, 9998887770', cur);
          if (v === null) return;
          localStorage.setItem(teamKey, v);
          toast.success(`✅ ${getTeam().length} team numbers saved`);
        };
        const team = getTeam();
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              data-testid="vyapar-share-daily-galla"
              onClick={async () => {
                try { const msg = await buildDailyMsg(); window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank'); }
                catch { toast.error('Failed to build daily galla'); }
              }}
              className="sm:col-span-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg hover:from-emerald-400 hover:to-teal-400"
            >
              <Share2 className="w-4 h-4" /> 📊 Aaj ka Galla WhatsApp par Share karein
            </button>
            <div className="flex gap-1">
              <button
                data-testid="vyapar-galla-team-send"
                onClick={async () => {
                  const t = getTeam();
                  if (t.length === 0) { toast.error('Pehle team numbers save karein (⚙️ button)'); return; }
                  if (!window.confirm(`Send to ${t.length} team members?\n\n${t.length} alag tabs khulenge — har tab mein Send press karna hoga.`)) return;
                  try {
                    const msg = await buildDailyMsg();
                    t.forEach((num, i) => {
                      setTimeout(() => {
                        const norm = num.length === 10 ? '91' + num : num;
                        window.open(`https://wa.me/${norm}?text=${encodeURIComponent(msg)}`, '_blank');
                      }, i * 400);
                    });
                  } catch { toast.error('Bulk send failed'); }
                }}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold text-xs flex items-center justify-center gap-1 shadow-lg"
                title="Send daily galla to saved team numbers">
                👥 Team ({team.length})
              </button>
              <button
                data-testid="vyapar-galla-team-edit"
                onClick={editTeam}
                className="px-3 py-2.5 rounded-xl bg-white/10 text-slate-200 text-xs hover:bg-white/20"
                title="Team WhatsApp numbers edit karein">⚙️</button>
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Users}       label="Customers" value={stats.customers} accent="text-fuchsia-300" />
        <StatCard icon={FileText}    label="Invoices"  value={stats.invoices} accent="text-amber-300" />
        <StatCard icon={Wallet}      label="Total Udhari" value={`₹${stats.total_due}`} accent="text-emerald-300" />
        <StatCard icon={AlertTriangle} label="Defaulters" value={stats.defaulters} accent="text-red-300" />
      </div>

      {/* Digital Narad — Upcoming Events */}
      {(events.birthdays.length > 0 || events.anniversaries.length > 0) && (
        <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-pink-500/10 p-4" data-testid="vyapar-events">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span className="font-bold text-sm">Digital Narad — Aane wale Special Days (30 din)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase text-amber-300 mb-1 font-bold">🎂 Birthdays ({events.birthdays.length})</div>
              {events.birthdays.length === 0 && <div className="text-xs text-slate-500">Koi upcoming birthday nahi</div>}
              <div className="space-y-1">
                {events.birthdays.slice(0, 5).map((b) => (
                  <div key={b.customer_id} className="flex items-center justify-between bg-white/5 rounded px-2 py-1.5 text-xs">
                    <div><b>{b.name}</b> <span className="text-slate-400 ml-1">· {new Date(b.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} ({b.days_left}d)</span></div>
                    <button onClick={() => sendWish(b, 'birthday')} data-testid={`wish-bday-${b.customer_id}`} className="text-emerald-300 text-[10px] font-bold">📤 Wish</button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase text-pink-300 mb-1 font-bold">💐 Anniversaries ({events.anniversaries.length})</div>
              {events.anniversaries.length === 0 && <div className="text-xs text-slate-500">Koi upcoming anniversary nahi</div>}
              <div className="space-y-1">
                {events.anniversaries.slice(0, 5).map((a) => (
                  <div key={a.customer_id} className="flex items-center justify-between bg-white/5 rounded px-2 py-1.5 text-xs">
                    <div><b>{a.name}</b> <span className="text-slate-400 ml-1">· {new Date(a.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} ({a.days_left}d)</span></div>
                    <button onClick={() => sendWish(a, 'anniversary')} data-testid={`wish-ann-${a.customer_id}`} className="text-emerald-300 text-[10px] font-bold">📤 Wish</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <QuickAction icon={Plus}     title="Add Customer"    desc="Naya grahak add karein (DOB/Anniversary bhi zaroor bharein!)" onClick={() => onSwitchTab('customers')} testid="qa-add-customer" />
        <QuickAction icon={FileText} title="Naya Bill"       desc="Sale voucher banayein" onClick={() => onSwitchTab('billing')} testid="qa-new-bill" />
        <QuickAction icon={QrCode}   title="Magic QR"        desc="Counter par lagayein, ghraak khud add karega" onClick={() => onSwitchTab('qr')} testid="qa-magic-qr" />
        <QuickAction icon={Wallet}   title="Udhari Bahi"     desc="Sabki udhari dekhein aur WhatsApp reminder bhejein" onClick={() => onSwitchTab('ledger')} testid="qa-udhari" />
      </div>
      {plan?.is_trial && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 flex flex-wrap items-center gap-3" data-testid="vyapar-trial-nudge">
          <Crown className="w-5 h-5 text-emerald-300" />
          <div className="flex-1">
            <div className="font-bold text-sm text-emerald-200">Aap trial par hain</div>
            <div className="text-xs text-emerald-100/80">Trial khatam hone se pehle plan lein — Yearly plan mein DFY (Done-For-You) onboarding FREE! 500 customers hum feed karke denge.</div>
          </div>
          <button onClick={() => onSwitchTab('plans')} data-testid="trial-upgrade-btn" className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white font-bold text-xs">Upgrade Now</button>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/50 p-3">
    <div className="flex items-center gap-2 mb-1"><Icon className={`w-4 h-4 ${accent}`} /><span className="text-[11px] uppercase tracking-widest text-slate-400">{label}</span></div>
    <div className="text-2xl font-extrabold">{value}</div>
  </div>
);
const QuickAction = ({ icon: Icon, title, desc, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="text-left rounded-xl border border-white/10 bg-slate-900/60 p-3 hover:border-fuchsia-400/50 hover:bg-slate-900/80 transition">
    <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-fuchsia-300" /><span className="font-bold text-sm">{title}</span><ChevronRight className="w-3 h-3 text-slate-400 ml-auto" /></div>
    <div className="text-[11px] text-slate-400 mt-1">{desc}</div>
  </button>
);

// ============ Customers Tab ============
const CustomersTab = ({ workspace }) => {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('');
  const [eventFilter, setEventFilter] = useState(''); // '' | 'birthday' | 'anniversary' — this month
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tags, setTags] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [showBulk, setShowBulk] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/vyapar/customers?workspace_id=${workspace.id}${q ? `&q=${encodeURIComponent(q)}` : ''}${tag ? `&tag=${tag}` : ''}&limit=500`);
      setCustomers(r.data.customers);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
  }, [workspace.id, q, tag]);
  useEffect(() => {
    load();
    api.get('/vyapar/plans').then((r) => setTags(r.data.default_tags || []));
    api.get(`/vyapar/custom-fields?workspace_id=${workspace.id}&applies_to=customer`).then((r) => setCustomFields(r.data.fields || [])).catch(() => {});
  }, [load, workspace.id]);

  // Client-side birthday/anniversary filter (current month + next 30 days)
  const filtered = useMemo(() => {
    if (!eventFilter) return customers;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const horizon = new Date(today.getTime() + 30 * 86400000);
    return customers.filter((c) => {
      const val = eventFilter === 'birthday' ? c.dob : c.anniversary;
      if (!val || String(val).length < 10) return false;
      const m = parseInt(String(val).slice(5, 7), 10);
      const d = parseInt(String(val).slice(8, 10), 10);
      if (!m || !d) return false;
      const year = today.getFullYear();
      let candidate = new Date(year, m - 1, d);
      if (candidate < today) candidate = new Date(year + 1, m - 1, d);
      return candidate <= horizon;
    });
  }, [customers, eventFilter]);

  const sendWhatsApp = (c) => {
    const num = String(c.whatsapp || c.mobile || '').replace(/\D/g, '');
    if (!num) return toast.error('Mobile nahi hai');
    const msg = `Namaste ${c.name} 🙏\n\n*${workspace.business_name}* ki taraf se hardik namaskar. Aapki kya seva kar sakte hain?\n\n_Sent via Anant Vyapar_`;
    const url = `https://wa.me/${num.length === 10 ? '91' + num : num}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  const exportCustomers = async (fmt) => {
    if (filtered.length === 0) return toast.error('Koi customer nahi hai export ke liye');
    if (fmt === 'csv') {
      const header = ['Sr','Name','Mobile','WhatsApp','DOB','Anniversary','LTV','Tags','Notes'];
      const rows = filtered.map((c, i) => [i+1, c.name, c.mobile, c.whatsapp || '', c.dob || '', c.anniversary || '', c.ltv || 0, (c.tags || []).join('|'), (c.notes || '').replace(/[\r\n,]/g,' ')]);
      const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${workspace.business_name}-customers.csv`; a.click();
      toast.success('CSV downloaded');
    } else if (fmt === 'pdf') {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF();
      doc.text(`${workspace.business_name} — Customer List`, 14, 15);
      autoTable(doc, {
        startY: 22,
        head: [['Sr','Name','Mobile','LTV','Tags']],
        body: filtered.map((c, i) => [i+1, c.name, c.mobile, `₹${c.ltv || 0}`, (c.tags || []).join(', ')]),
      });
      doc.save(`${workspace.business_name}-customers.pdf`);
      toast.success('PDF downloaded');
    }
  };

  return (
    <div className="space-y-3" data-testid="vyapar-customers-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[180px] relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Naam ya mobile search karein" data-testid="cust-search" className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" />
        </div>
        <select value={tag} onChange={(e) => setTag(e.target.value)} data-testid="cust-tag-filter" className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm">
          <option value="" className="bg-slate-900">All tags</option>
          {tags.map((t) => <option key={t.id} value={t.id} className="bg-slate-900">{t.emoji} {t.label}</option>)}
        </select>
        <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} data-testid="cust-event-filter" className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm">
          <option value="" className="bg-slate-900">All customers</option>
          <option value="birthday" className="bg-slate-900">🎂 Birthday (30 din)</option>
          <option value="anniversary" className="bg-slate-900">💐 Anniversary (30 din)</option>
        </select>
        <button onClick={() => setShowAdd(true)} data-testid="cust-add-btn" className="px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold text-sm flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add
        </button>
        <button onClick={() => setShowBulk(true)} data-testid="cust-bulk-btn" className="px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm flex items-center gap-1">
          <Package className="w-4 h-4" /> Bulk Upload
        </button>
        <button onClick={() => exportCustomers('csv')} data-testid="cust-export-csv" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
        <button onClick={() => exportCustomers('pdf')} data-testid="cust-export-pdf" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm flex items-center gap-1"><Download className="w-3 h-3" /> PDF</button>
      </div>
      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-white/5 text-xs uppercase text-slate-400">
            <tr><th className="text-left px-3 py-2">#</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Mobile</th><th className="text-left px-3 py-2">🎂 DOB</th><th className="text-left px-3 py-2">💐 Anniv.</th><th className="text-left px-3 py-2">Tags</th><th className="text-right px-3 py-2">LTV</th><th className="px-3 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500 text-xs">Koi customer nahi mila. "Add" ya "Bulk Upload" par click karein.</td></tr>}
            {filtered.map((c, i) => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                <td className="px-3 py-2 font-bold">{c.name}</td>
                <td className="px-3 py-2">{c.mobile}</td>
                <td className="px-3 py-2 text-xs text-amber-300">{c.dob ? new Date(c.dob).toLocaleDateString('en-IN', {day:'numeric', month:'short'}) : '—'}</td>
                <td className="px-3 py-2 text-xs text-pink-300">{c.anniversary ? new Date(c.anniversary).toLocaleDateString('en-IN', {day:'numeric', month:'short'}) : '—'}</td>
                <td className="px-3 py-2">{(c.tags || []).map((t) => { const tt = tags.find((x) => x.id === t); return tt ? <span key={t} className="mr-1 text-[10px] px-1.5 py-0.5 rounded" style={{ background: tt.color+'22', color: tt.color }}>{tt.emoji} {tt.label}</span> : null; })}</td>
                <td className="px-3 py-2 text-right text-emerald-300">₹{c.ltv || 0}</td>
                <td className="px-3 py-2 flex gap-1 justify-center items-center">
                  <button onClick={() => sendWhatsApp(c)} data-testid={`cust-wa-${c.id}`} title="Send WhatsApp"
                    className="p-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-300"><Share2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setSelected(c)} data-testid={`cust-edit-${c.id}`} className="p-1 rounded hover:bg-white/10"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={async () => { if (window.confirm('Delete customer?')) { await api.delete(`/vyapar/customers/${c.id}`); toast.success('Deleted'); load(); } }} data-testid={`cust-del-${c.id}`} className="p-1 rounded hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(showAdd || selected) && (
        <CustomerFormModal
          workspace={workspace}
          initial={selected}
          tags={tags}
          customFields={customFields}
          onClose={() => { setShowAdd(false); setSelected(null); }}
          onSaved={() => { setShowAdd(false); setSelected(null); load(); }}
        />
      )}
      {showBulk && (
        <BulkUploadModal workspace={workspace} onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); load(); }} />
      )}
    </div>
  );
};

// ============ Bulk Upload Modal ============
const BulkUploadModal = ({ workspace, onClose, onDone }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseCSV = (text) => {
    // Simple CSV parser supporting quoted fields (handles commas & escapes inside quotes)
    const out = [];
    let row = [], cur = '', inQ = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i], nx = text[i + 1];
      if (inQ) {
        if (ch === '"' && nx === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQ = false; }
        else cur += ch;
      } else {
        if (ch === '"') inQ = true;
        else if (ch === ',') { row.push(cur); cur = ''; }
        else if (ch === '\n' || ch === '\r') {
          if (cur.length || row.length) { row.push(cur); out.push(row); row = []; cur = ''; }
          if (ch === '\r' && nx === '\n') i++;
        } else cur += ch;
      }
    }
    if (cur.length || row.length) { row.push(cur); out.push(row); }
    return out.filter((r) => r.some((c) => String(c).trim().length));
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const parsed = parseCSV(text);
      if (parsed.length < 2) return toast.error('File empty ya invalid hai');
      const headerRaw = parsed[0].map((h) => String(h).trim().toLowerCase());
      const findCol = (...names) => {
        for (const n of names) { const i = headerRaw.indexOf(n); if (i !== -1) return i; }
        return -1;
      };
      const iName = findCol('name', 'naam', 'customer name');
      const iMobile = findCol('mobile', 'phone', 'number', 'mobile number');
      const iWA = findCol('whatsapp', 'wa');
      const iDob = findCol('dob', 'birthday', 'date of birth');
      const iAnv = findCol('anniversary', 'marriage anniversary');
      const iNotes = findCol('notes', 'note', 'remark');
      const iCredit = findCol('credit_limit', 'credit limit', 'udhari limit');
      if (iName === -1 || iMobile === -1) return toast.error('Header me "name" aur "mobile" columns zaroori hain');
      const parsedRows = parsed.slice(1).map((r) => ({
        name: (r[iName] || '').trim(),
        mobile: (r[iMobile] || '').trim(),
        whatsapp: iWA !== -1 ? (r[iWA] || '').trim() : '',
        dob: iDob !== -1 ? (r[iDob] || '').trim() : '',
        anniversary: iAnv !== -1 ? (r[iAnv] || '').trim() : '',
        notes: iNotes !== -1 ? (r[iNotes] || '').trim() : '',
        credit_limit: iCredit !== -1 ? (parseFloat(r[iCredit]) || null) : null,
        tags: ['regular'],
      })).filter((c) => c.name && c.mobile);
      setRows(parsedRows);
      toast.success(`${parsedRows.length} rows parse ho gaye · Preview neeche dekhein`);
    };
    reader.readAsText(file);
  };

  const upload = async () => {
    if (rows.length === 0) return toast.error('Pehle file select karein');
    setBusy(true);
    try {
      const r = await api.post(`/vyapar/customers/bulk-upload?workspace_id=${workspace.id}`, { customers: rows });
      setResult(r.data);
      toast.success(`✅ ${r.data.created} customers add ho gaye`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = 'name,mobile,whatsapp,dob,anniversary,notes,credit_limit\n"Rahul Sharma","9998887770","9998887770","1990-05-15","2015-11-20","VIP","5000"\n"Priya Devi","9998887771","","1988-03-12","","",""';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'anant-vyapar-customer-template.csv'; a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-upload-modal">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-bold">📥 Bulk Customer Upload</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="rounded-lg bg-amber-500/10 border border-amber-400/30 p-3 text-xs text-amber-100 mb-3">
          <b>Kaise use karein?</b>
          <ol className="list-decimal ml-4 mt-1 space-y-0.5">
            <li>Neeche se template download karein</li>
            <li>Apna data Excel/Google Sheets me fill karein</li>
            <li>File ko CSV format me save karein (Excel: File → Save As → CSV)</li>
            <li>Yahaan upload karein — <b>Unlimited rows allowed</b></li>
          </ol>
          <div className="mt-1.5">Required: <b>name</b>, <b>mobile</b>. Optional: whatsapp, dob (YYYY-MM-DD), anniversary, notes, credit_limit</div>
        </div>
        <div className="flex gap-2 mb-3">
          <button onClick={downloadTemplate} data-testid="bulk-tpl-btn" className="flex-1 px-3 py-2 rounded-lg bg-white/10 text-xs font-bold flex items-center justify-center gap-1"><Download className="w-3.5 h-3.5" /> Download Template</button>
        </div>
        <label className="block">
          <input type="file" accept=".csv,text/csv,text/plain,.tsv" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="bulk-file-input" className="block w-full text-xs text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-fuchsia-500 file:text-white file:font-bold file:cursor-pointer" />
        </label>
        {rows.length > 0 && (
          <div className="mt-3 rounded-lg border border-white/10 max-h-40 overflow-y-auto" data-testid="bulk-preview">
            <div className="p-2 text-xs bg-white/5 font-bold">Preview: {rows.length} rows detected</div>
            <table className="w-full text-[11px]">
              <thead className="bg-white/5"><tr><th className="text-left px-2 py-1">Name</th><th className="text-left px-2 py-1">Mobile</th><th className="text-left px-2 py-1">DOB</th></tr></thead>
              <tbody>{rows.slice(0, 20).map((r, i) => (
                <tr key={i} className="border-t border-white/5"><td className="px-2 py-1">{r.name}</td><td className="px-2 py-1">{r.mobile}</td><td className="px-2 py-1">{r.dob || '—'}</td></tr>
              ))}</tbody>
            </table>
            {rows.length > 20 && <div className="text-[10px] text-slate-500 p-1 text-center">+ {rows.length - 20} more…</div>}
          </div>
        )}
        {result && (
          <div className="mt-3 rounded-lg border border-emerald-400/40 bg-emerald-500/10 p-3 text-xs text-emerald-100 space-y-2" data-testid="bulk-result">
            <div>✅ <b>Created:</b> {result.created} · <b>Duplicates skipped:</b> {result.skipped_duplicates} · <b>Invalid skipped:</b> {result.skipped_invalid}</div>
            {result.created > 0 && rows.length > 0 && (
              <div className="border-t border-emerald-400/20 pt-2">
                <div className="font-bold text-emerald-200 mb-1">🎁 Referral Loop — Send welcome + your referral link</div>
                <div className="text-[10px] text-emerald-100/80 mb-1.5">Naye customers ko welcome WhatsApp bhejein. Har message aapke Anant Web Portal referral link ke saath jayega (aapki Affiliate Network earning bhi badhegi).</div>
                <button
                  onClick={() => {
                    const refUser = user?.username || '';
                    const refLink = refUser ? `${window.location.origin}/register?ref=${refUser}` : window.location.origin;
                    const validRows = rows.filter((r) => r.name && r.mobile && r.mobile.length >= 10);
                    if (validRows.length === 0) return toast.error('Koi valid mobile nahi hai');
                    validRows.slice(0, 20).forEach((r, i) => {
                      const num = String(r.mobile).replace(/\D/g, '');
                      const wa = num.length === 10 ? '91' + num : num;
                      const msg = `Namaste ${r.name} 🙏\n\n*${workspace.business_name}* mein aapka swagat hai. Humari seva ka laabh uthate rahiyega.\n\n🎁 Special offer: Anant Web Portal join karein aur exclusive discounts payein — ${refLink}\n\nDhanyawad!\n_Sent via Anant Vyapar_`;
                      setTimeout(() => window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, '_blank'), i * 400);
                    });
                    toast.success(`${Math.min(20, validRows.length)} WhatsApp tabs khul rahe hain — send karte raho 🚀`);
                  }}
                  data-testid="bulk-welcome-wa"
                  className="w-full py-2 rounded-lg bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-1"
                >
                  <Share2 className="w-3.5 h-3.5" /> Send Welcome WA to {Math.min(20, result.created)} customers (with referral link)
                </button>
                <div className="text-[9px] text-emerald-100/60 italic mt-1">Note: Ek baar mein 20 tabs khulenge (browser limit). Ye referral link aapki Affiliate Network webchain ko boost karega.</div>
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2 mt-3">
          <button onClick={onDone} className="flex-1 py-2 rounded-lg bg-white/10 text-xs font-bold">Close</button>
          <button onClick={upload} disabled={busy || rows.length === 0} data-testid="bulk-upload-btn" className="flex-1 py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm disabled:opacity-50">
            {busy ? 'Uploading…' : `Upload ${rows.length} rows`}
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomerFormModal = ({ workspace, initial, tags, customFields = [], onClose, onSaved }) => {
  const [form, setForm] = useState(initial || { name: '', mobile: '', whatsapp: '', dob: '', anniversary: '', notes: '', tags: [], custom: {}, credit_limit: '', credit_due_date: '' });
  const [busy, setBusy] = useState(false);
  const [defaulterWarn, setDefaulterWarn] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCustom = (k, v) => setForm((f) => ({ ...f, custom: { ...(f.custom || {}), [k]: v } }));
  const toggleTag = (id) => setForm((f) => ({ ...f, tags: f.tags?.includes(id) ? f.tags.filter((t) => t !== id) : [...(f.tags || []), id] }));
  useEffect(() => {
    if (!initial && form.mobile && form.mobile.length >= 10) {
      api.get(`/vyapar/customers/check-defaulter/${form.mobile}`).then((r) => setDefaulterWarn(r.data.is_defaulter ? r.data.info : null)).catch(() => {});
    }
  }, [form.mobile, initial]);
  const submit = async () => {
    if (!form.name || !form.mobile) return toast.error('Naam aur mobile zaroori hai');
    setBusy(true);
    const payload = { ...form, credit_limit: form.credit_limit === '' ? null : Number(form.credit_limit) };
    try {
      if (initial) {
        await api.put(`/vyapar/customers/${initial.id}`, payload);
        toast.success('Updated');
      } else {
        const r = await api.post(`/vyapar/customers?workspace_id=${workspace.id}`, payload);
        if (r.data.defaulter_warning) toast.warning(`⚠️ Ye customer network par defaulter hai (${r.data.defaulter_warning.reported_by})`);
        else toast.success('Customer added');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cust-form-modal">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-bold">{initial ? 'Edit Customer' : 'Add Customer'}</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        {defaulterWarn && (
          <div className="mb-3 rounded-lg border border-red-400/40 bg-red-500/10 p-2 text-xs text-red-200">
            ⚠️ WARNING: Ye mobile Anant network par defaulter hai. Reported by <b>{defaulterWarn.reported_by}</b>. Reason: {defaulterWarn.reason || 'N/A'}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="col-span-2">
            <label className="text-[10px] text-slate-400 uppercase font-bold">Name *</label>
            <input placeholder="Full name" value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="cust-form-name" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase font-bold">Mobile *</label>
            <input placeholder="10-digit" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} data-testid="cust-form-mobile" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase font-bold">WhatsApp</label>
            <input placeholder="Optional" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] text-amber-300 uppercase font-bold flex items-center gap-1">🎂 DOB (Birthday)</label>
            <input type="date" value={form.dob || ''} onChange={(e) => set('dob', e.target.value)} data-testid="cust-form-dob" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] text-pink-300 uppercase font-bold flex items-center gap-1">💐 Anniversary</label>
            <input type="date" value={form.anniversary || ''} onChange={(e) => set('anniversary', e.target.value)} data-testid="cust-form-anv" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] text-red-300 uppercase font-bold">Credit Limit (₹)</label>
            <input type="number" placeholder="Udhari max limit" value={form.credit_limit || ''} onChange={(e) => set('credit_limit', e.target.value)} data-testid="cust-form-credit-limit" className="w-full bg-red-500/5 border border-red-400/30 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] text-red-300 uppercase font-bold">Credit Due Date</label>
            <input type="date" value={form.credit_due_date || ''} onChange={(e) => set('credit_due_date', e.target.value)} data-testid="cust-form-credit-due" className="w-full bg-red-500/5 border border-red-400/30 rounded-lg px-3 py-2" />
          </div>
          <textarea placeholder="Notes" value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          {customFields.length > 0 && (
            <div className="col-span-2 border-t border-white/10 pt-2">
              <div className="text-[10px] uppercase font-bold text-fuchsia-300 mb-1.5">Custom Fields</div>
              <div className="grid grid-cols-2 gap-2">
                {customFields.map((cf) => {
                  const val = (form.custom || {})[cf.label] ?? '';
                  if (cf.field_type === 'select') {
                    return (
                      <select key={cf.id} value={val} onChange={(e) => setCustom(cf.label, e.target.value)} data-testid={`cf-input-${cf.id}`} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                        <option value="" className="bg-slate-900">{cf.label}…</option>
                        {(cf.options || []).map((o) => <option key={o} value={o} className="bg-slate-900">{o}</option>)}
                      </select>
                    );
                  }
                  if (cf.field_type === 'image_url') {
                    return (
                      <div key={cf.id} className="col-span-2">
                        <label className="text-[9px] text-slate-400">📷 {cf.label} (Image URL)</label>
                        <input type="url" placeholder="https://…" value={val} onChange={(e) => setCustom(cf.label, e.target.value)} data-testid={`cf-input-${cf.id}`} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
                        {val && <img src={val} alt={cf.label} className="mt-1 h-16 rounded border border-white/10 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
                      </div>
                    );
                  }
                  if (cf.field_type === 'timer') {
                    return (
                      <div key={cf.id}>
                        <label className="text-[9px] text-amber-300">⏳ {cf.label} (Countdown target)</label>
                        <input type="datetime-local" value={val} onChange={(e) => setCustom(cf.label, e.target.value)} data-testid={`cf-input-${cf.id}`} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
                        {val && (() => {
                          const diff = new Date(val).getTime() - Date.now();
                          if (isNaN(diff)) return null;
                          const abs = Math.abs(diff);
                          const d = Math.floor(abs / 86400000);
                          const h = Math.floor((abs % 86400000) / 3600000);
                          return <div className={`text-[10px] mt-0.5 ${diff > 0 ? 'text-emerald-300' : 'text-red-300'}`}>{diff > 0 ? `⏱️ ${d}d ${h}h remaining` : `⚠️ Overdue by ${d}d ${h}h`}</div>;
                        })()}
                      </div>
                    );
                  }
                  const inputType = cf.field_type === 'number' ? 'number'
                    : cf.field_type === 'date' ? 'date'
                    : (cf.field_type === 'url' || cf.field_type === 'link') ? 'url'
                    : 'text';
                  return (
                    <div key={cf.id}>
                      <label className="text-[9px] text-slate-400">{cf.field_type === 'url' ? '🔗 ' : cf.field_type === 'link' ? '🌐 ' : ''}{cf.label}</label>
                      <input
                        type={inputType}
                        placeholder={cf.label} value={val}
                        onChange={(e) => setCustom(cf.label, e.target.value)}
                        data-testid={`cf-input-${cf.id}`}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2"
                      />
                      {(cf.field_type === 'url' || cf.field_type === 'link') && val && (
                        <a href={val} target="_blank" rel="noreferrer" className="text-[10px] text-blue-300 underline break-all">Open link ↗</a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="col-span-2 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <button key={t.id} onClick={() => toggleTag(t.id)} data-testid={`cust-tag-${t.id}`}
                className={`text-[11px] px-2 py-1 rounded-full border transition ${form.tags?.includes(t.id) ? 'border-white/40' : 'border-white/10 opacity-50'}`}
                style={{ background: t.color+'22', color: t.color }}>
                {t.emoji} {t.label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="cust-form-save" className="w-full mt-3 py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm disabled:opacity-60">
          {busy ? 'Saving…' : (initial ? 'Update' : 'Add Customer')}
        </button>
      </div>
    </div>
  );
};

// ============ Billing Tab ============
const BillingTab = ({ workspace }) => {
  const [invoices, setInvoices] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/vyapar/invoices?workspace_id=${workspace.id}`);
      setInvoices(r.data.invoices);
    } catch {}
  }, [workspace.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="vyapar-billing-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-400">Total Invoices: <b className="text-white">{invoices.length}</b></div>
        <button onClick={() => setShowCreate(true)} data-testid="bill-create-btn" className="px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-bold text-sm flex items-center gap-1">
          <Plus className="w-4 h-4" /> Naya Bill
        </button>
      </div>
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase text-slate-400">
            <tr><th className="text-left px-3 py-2">Invoice#</th><th className="text-left px-3 py-2">Customer</th><th className="text-right px-3 py-2">Total</th><th className="text-right px-3 py-2">Paid</th><th className="text-right px-3 py-2">Due</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500 text-xs">Koi invoice nahi banaya. Naya Bill par click karein.</td></tr>}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_no}</td>
                <td className="px-3 py-2">{inv.customer_name}</td>
                <td className="px-3 py-2 text-right">₹{inv.total}</td>
                <td className="px-3 py-2 text-right text-emerald-300">₹{inv.paid}</td>
                <td className="px-3 py-2 text-right text-red-300">₹{inv.due}</td>
                <td className="px-3 py-2 text-xs text-slate-400">{new Date(inv.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-center"><button onClick={() => setSelected(inv)} data-testid={`bill-view-${inv.id}`} className="text-fuchsia-300 text-xs font-bold">View · Share</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCreate && <InvoiceCreateModal workspace={workspace} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {selected && <InvoiceViewModal invoice={selected} workspace={workspace} onClose={() => setSelected(null)} />}
    </div>
  );
};

const InvoiceCreateModal = ({ workspace, onClose, onCreated }) => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [items, setItems] = useState([{ name: '', rate: 0, qty: 1, discount: 0 }]);
  const [overallDiscount, setOverallDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [paid, setPaid] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/vyapar/customers?workspace_id=${workspace.id}&limit=500`).then((r) => setCustomers(r.data.customers)); }, [workspace.id]);
  const subtotal = items.reduce((s, it) => s + Math.max(0, it.rate * it.qty - it.discount), 0);
  const total = Math.max(0, subtotal - overallDiscount);
  const setItem = (i, k, v) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addRow = () => setItems([...items, { name: '', rate: 0, qty: 1, discount: 0 }]);
  const submit = async () => {
    if (!customerId) return toast.error('Customer select karein');
    if (items.some((it) => !it.name || it.rate <= 0)) return toast.error('Sabhi items ka naam aur rate daalein');
    setBusy(true);
    try {
      const payload = { customer_id: customerId, items, overall_discount: Number(overallDiscount)||0, notes, paid_amount: Number(paid)||0 };
      await api.post(`/vyapar/invoices?workspace_id=${workspace.id}`, payload);
      toast.success('Invoice created ✅');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bill-create-modal">
        <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-bold">Naya Sale Voucher</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase font-bold text-emerald-300">🎤 Voice fill</div>
          <VoiceInputHelper label="Fill by Voice"
            onDictate={(text) => {
              const parsed = parseNumericFields(text, {
                amount: ['amount', 'rate', 'price', 'रकम', 'ki', 'rupees', 'rupaye'],
                qty:    ['qty', 'quantity', 'nag', 'number of', 'सामान', 'sankhya'],
                paid:   ['paid', 'received', 'diya', 'दिया'],
                discount:['discount', 'chhoot', 'छूट', 'kam'],
              });
              // customer @username OR customer name
              const nameM = text.match(/customer\s+@?([\w\s]+?)(?=\s+(?:amount|rate|qty|paid|discount|item|product)\b|$)/i);
              const itemM = text.match(/item\s+([\w\s]+?)(?=\s+(?:amount|rate|qty|paid|discount)\b|$)/i);
              let touched = [];
              if (nameM) {
                const nm = nameM[1].trim().toLowerCase();
                const match = customers.find((c) => (c.name || '').toLowerCase().includes(nm) || (c.mobile || '').includes(nm));
                if (match) { setCustomerId(match.id); touched.push('customer'); }
              }
              const patch = { ...(items[0] || { name: '', rate: 0, qty: 1, discount: 0 }) };
              if (itemM) { patch.name = itemM[1].trim(); touched.push('item'); }
              if (parsed.amount) { patch.rate = parseFloat(parsed.amount); touched.push('rate'); }
              if (parsed.qty) { patch.qty = parseInt(parsed.qty); touched.push('qty'); }
              if (parsed.discount) { patch.discount = parseFloat(parsed.discount); touched.push('discount'); }
              if (touched.some((t) => ['item','rate','qty','discount'].includes(t))) { setItems([patch, ...items.slice(1)]); }
              if (parsed.paid) { setPaid(parseFloat(parsed.paid)); touched.push('paid'); }
              if (touched.length) toast.success(`🎤 Filled: ${touched.join(', ')}`);
              else toast.error('🎤 Say: "customer Ram, item Aata, amount 500, qty 2, paid 300"');
            }}
          />
        </div>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="bill-customer-select" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm mb-3">
          <option value="" className="bg-slate-900">Customer select karein</option>
          {customers.map((c) => <option key={c.id} value={c.id} className="bg-slate-900">{c.name} · {c.mobile}</option>)}
        </select>
        {(workspace.quick_items || []).length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] uppercase font-bold text-fuchsia-300 mb-1">⚡ Quick Items (tap to add)</div>
            <div className="flex flex-wrap gap-1.5">
              {workspace.quick_items.map((qi, i) => (
                <button key={i} onClick={() => setItems((prev) => {
                  const blank = prev.length === 1 && !prev[0].name && !prev[0].rate;
                  const row = { name: qi.name, rate: qi.rate, qty: 1, discount: 0 };
                  return blank ? [row] : [...prev, row];
                })} data-testid={`quick-item-${i}`}
                  className="text-[11px] px-2 py-1 rounded-full bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-400/30 hover:bg-fuchsia-500/30 transition">
                  + {qi.name} · ₹{qi.rate}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="rounded-lg border border-white/10 overflow-hidden mb-3">
          <table className="w-full text-xs">
            <thead className="bg-white/5"><tr><th className="text-left px-2 py-1">Item</th><th className="px-2 py-1">Rate</th><th className="px-2 py-1">Qty</th><th className="px-2 py-1">Disc</th><th className="text-right px-2 py-1">Total</th><th></th></tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-1 py-1"><input value={it.name} onChange={(e) => setItem(i, 'name', e.target.value)} data-testid={`item-name-${i}`} className="w-full bg-white/5 rounded px-2 py-1" /></td>
                  <td className="px-1 py-1"><input type="number" value={it.rate} onChange={(e) => setItem(i, 'rate', Number(e.target.value))} className="w-20 bg-white/5 rounded px-2 py-1" /></td>
                  <td className="px-1 py-1"><input type="number" value={it.qty} onChange={(e) => setItem(i, 'qty', Number(e.target.value))} className="w-16 bg-white/5 rounded px-2 py-1" /></td>
                  <td className="px-1 py-1"><input type="number" value={it.discount} onChange={(e) => setItem(i, 'discount', Number(e.target.value))} className="w-16 bg-white/5 rounded px-2 py-1" /></td>
                  <td className="px-2 py-1 text-right">₹{Math.max(0, it.rate * it.qty - it.discount).toFixed(2)}</td>
                  <td className="px-1 py-1"><button onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-red-300"><X className="w-3 h-3" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={addRow} data-testid="bill-add-row" className="text-xs text-fuchsia-300 mb-3">+ Add item</button>
        <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
          <div><label className="text-xs text-slate-400">Overall Discount</label><input type="number" value={overallDiscount} onChange={(e) => setOverallDiscount(Number(e.target.value))} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1" /></div>
          <div><label className="text-xs text-slate-400">Paid Amount</label><input type="number" value={paid} onChange={(e) => setPaid(Number(e.target.value))} data-testid="bill-paid" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1" /></div>
        </div>
        <textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 mb-3 text-sm" />
        <div className="text-right font-bold mb-3">Total: ₹{total.toFixed(2)} · Paid: ₹{Number(paid)||0} · Due: ₹{Math.max(0, total - (Number(paid)||0)).toFixed(2)}</div>
        <button onClick={submit} disabled={busy} data-testid="bill-save" className="w-full py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm disabled:opacity-60">{busy ? 'Saving…' : 'Save Invoice'}</button>
      </div>
    </div>
  );
};

const InvoiceViewModal = ({ invoice, workspace, onClose }) => {
  const gstRate = invoice.gst_rate || 0;
  const isInterState = !!invoice.inter_state;
  const taxable = Number(invoice.subtotal || 0) - Number(invoice.overall_discount || 0);
  const totalTax = Math.round(taxable * gstRate) / 100;
  const cgst = isInterState ? 0 : Math.round(totalTax * 50) / 100;
  const sgst = isInterState ? 0 : totalTax - cgst;
  const igst = isInterState ? totalTax : 0;
  const grand = Math.round((taxable + totalTax) * 100) / 100;

  const downloadPdf = async () => {
    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    const doc = new jsPDF();
    // Govt-style TAX INVOICE header
    doc.setFontSize(16); doc.setFont(undefined, 'bold');
    doc.text('TAX INVOICE', 105, 15, { align: 'center' });
    doc.setFontSize(11); doc.setFont(undefined, 'normal');
    doc.text(workspace.business_name, 14, 25);
    if (workspace.gstin) doc.text(`GSTIN: ${workspace.gstin}`, 14, 30);
    if (workspace.state) doc.text(`State: ${workspace.state}`, 14, 35);
    doc.setFontSize(9);
    doc.text(`Invoice: ${invoice.invoice_no}`, 150, 25);
    doc.text(`Date: ${new Date(invoice.created_at).toLocaleDateString('en-IN')}`, 150, 30);
    // Bill to
    doc.setFont(undefined, 'bold'); doc.text('Bill To:', 14, 45); doc.setFont(undefined, 'normal');
    doc.text(`${invoice.customer_name}`, 14, 51);
    if (invoice.customer_mobile) doc.text(`Mobile: ${invoice.customer_mobile}`, 14, 56);
    if (invoice.customer_gstin) doc.text(`GSTIN: ${invoice.customer_gstin}`, 14, 61);
    autoTable(doc, {
      startY: 68,
      head: [['#', 'Item / Service', 'HSN', 'Qty', 'Rate', 'Disc', 'Amount']],
      body: invoice.items.map((it, i) => [
        i + 1, it.name, it.hsn_code || invoice.hsn_code || '—',
        it.qty, `₹${Number(it.rate).toFixed(2)}`,
        it.discount ? `₹${it.discount}` : '-',
        `₹${Number(it.line_total).toFixed(2)}`,
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255 },
    });
    const y = (doc.lastAutoTable?.finalY || 90) + 6;
    doc.setFontSize(9);
    doc.text(`Subtotal:`, 130, y);            doc.text(`₹${Number(invoice.subtotal).toFixed(2)}`, 195, y, { align: 'right' });
    if (invoice.overall_discount) { doc.text(`Discount:`, 130, y + 5); doc.text(`-₹${Number(invoice.overall_discount).toFixed(2)}`, 195, y + 5, { align: 'right' }); }
    doc.text(`Taxable Value:`, 130, y + 10);   doc.text(`₹${taxable.toFixed(2)}`, 195, y + 10, { align: 'right' });
    let yy = y + 15;
    if (gstRate > 0) {
      if (isInterState) {
        doc.text(`IGST @ ${gstRate}%:`, 130, yy); doc.text(`₹${igst.toFixed(2)}`, 195, yy, { align: 'right' }); yy += 5;
      } else {
        doc.text(`CGST @ ${gstRate / 2}%:`, 130, yy); doc.text(`₹${cgst.toFixed(2)}`, 195, yy, { align: 'right' }); yy += 5;
        doc.text(`SGST @ ${gstRate / 2}%:`, 130, yy); doc.text(`₹${sgst.toFixed(2)}`, 195, yy, { align: 'right' }); yy += 5;
      }
    }
    doc.setFontSize(11); doc.setFont(undefined, 'bold');
    doc.text(`GRAND TOTAL:`, 130, yy + 3); doc.text(`₹${grand.toFixed(2)}`, 195, yy + 3, { align: 'right' });
    doc.setFont(undefined, 'normal'); doc.setFontSize(9);
    doc.text(`Paid: ₹${Number(invoice.paid || 0).toFixed(2)}`, 130, yy + 10);
    if ((invoice.due || 0) > 0) doc.text(`Due: ₹${Number(invoice.due).toFixed(2)}`, 130, yy + 15);
    doc.setFontSize(7); doc.setTextColor(100, 100, 100);
    doc.text('Declaration: We hereby certify that our GST registration is in force and this invoice is a valid tax invoice.', 14, 275);
    doc.text('Generated by Anant Smart Vyapar System · A complete business management service.', 14, 282);
    doc.save(`${invoice.invoice_no}.pdf`);
    toast.success('✅ GST Tax Invoice downloaded');
  };
  const shareOnWA = () => {
    const text = `🧾 *${workspace.business_name}*\nInvoice: ${invoice.invoice_no}\nTaxable: ₹${taxable.toFixed(2)}\n${gstRate > 0 ? `GST @ ${gstRate}%: ₹${totalTax.toFixed(2)}\n` : ''}Total: ₹${grand.toFixed(2)}\nPaid: ₹${invoice.paid}\nDue: ₹${invoice.due}\n\nDhanyawad!\n_Anant Smart Vyapar System_`;
    const num = (invoice.customer_mobile || '').replace(/\D/g, '');
    const url = `https://wa.me/${num.length === 10 ? '91' + num : num}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bill-view-modal">
        <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-bold">🧾 TAX INVOICE · {invoice.invoice_no}</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        <div className="text-sm text-slate-300 space-y-1 mb-3">
          <div><b>{invoice.customer_name}</b> · {invoice.customer_mobile}</div>
          {invoice.customer_gstin && <div className="text-xs text-emerald-300 font-mono">GSTIN: {invoice.customer_gstin}</div>}
          <div className="text-xs text-slate-500">{new Date(invoice.created_at).toLocaleString('en-IN')}</div>
        </div>
        <div className="rounded-lg border border-white/10 overflow-hidden text-xs mb-3">
          <table className="w-full">
            <thead className="bg-white/5"><tr><th className="text-left px-2 py-1">Item</th><th className="px-2 py-1">HSN</th><th className="px-2 py-1">Qty</th><th className="text-right px-2 py-1">Total</th></tr></thead>
            <tbody>{invoice.items.map((it, i) => <tr key={i} className="border-t border-white/5"><td className="px-2 py-1">{it.name}</td><td className="text-center px-2 py-1 text-slate-400">{it.hsn_code || invoice.hsn_code || '—'}</td><td className="text-center px-2 py-1">{it.qty}</td><td className="text-right px-2 py-1">₹{it.line_total}</td></tr>)}</tbody>
          </table>
        </div>
        <div className="text-right text-sm mb-3 space-y-0.5">
          <div className="text-slate-400">Subtotal: ₹{Number(invoice.subtotal).toFixed(2)}</div>
          {invoice.overall_discount > 0 && <div className="text-slate-400">Discount: -₹{invoice.overall_discount}</div>}
          <div className="text-slate-300">Taxable: ₹{taxable.toFixed(2)}</div>
          {gstRate > 0 && !isInterState && <>
            <div className="text-emerald-300 text-xs">CGST @ {gstRate / 2}%: ₹{cgst.toFixed(2)}</div>
            <div className="text-emerald-300 text-xs">SGST @ {gstRate / 2}%: ₹{sgst.toFixed(2)}</div>
          </>}
          {gstRate > 0 && isInterState && <div className="text-emerald-300 text-xs">IGST @ {gstRate}%: ₹{igst.toFixed(2)}</div>}
          <div className="font-bold text-lg text-fuchsia-300 pt-1 border-t border-white/10 mt-1">Grand Total: ₹{grand.toFixed(2)}</div>
          <div className="text-emerald-300">Paid: ₹{invoice.paid}</div>
          {invoice.due > 0 && <div className="text-red-300">Due: ₹{invoice.due}</div>}
        </div>
        <div className="flex gap-2">
          <button onClick={downloadPdf} data-testid="bill-download-pdf" className="flex-1 py-2 rounded-lg bg-fuchsia-500 hover:bg-emerald-500 focus:bg-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-1 transition-colors"><Download className="w-4 h-4" /> GST PDF</button>
          <button onClick={shareOnWA} data-testid="bill-share-wa" className="flex-1 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-sm flex items-center justify-center gap-1"><Share2 className="w-4 h-4" /> WhatsApp</button>
        </div>
      </div>
    </div>
  );
};

// ============ Ledger Tab ============
const LedgerTab = ({ workspace }) => {
  const [summary, setSummary] = useState({ rows: [], total_due: 0, total_advance: 0 });
  const [reminders, setReminders] = useState({ upcoming: [], overdue: [] });
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [entryForm, setEntryForm] = useState({ customer_id: '', entry_type: 'paise_liye', amount: '', note: '', entry_date: new Date().toISOString().slice(0, 10), account_id: '' });
  const [entryBusy, setEntryBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [showAccMgr, setShowAccMgr] = useState(false);
  const [newAcc, setNewAcc] = useState({ name: '', account_type: 'cash', balance: '' });
  const load = useCallback(async () => {
    try {
      const [r, rem, cust, accs] = await Promise.all([
        api.get(`/vyapar/ledger/summary?workspace_id=${workspace.id}`),
        api.get(`/vyapar/udhari-reminders?workspace_id=${workspace.id}&days=3`).catch(() => ({ data: { upcoming: [], overdue: [] } })),
        api.get(`/vyapar/customers?workspace_id=${workspace.id}&limit=500`).catch(() => ({ data: { customers: [] } })),
        api.get(`/vyapar/accounts?workspace_id=${workspace.id}`).catch(() => ({ data: { accounts: [] } })),
      ]);
      setSummary(r.data);
      setReminders(rem.data || { upcoming: [], overdue: [] });
      setCustomers(cust.data.customers || []);
      setAccounts(accs.data.accounts || []);
    } catch {}
  }, [workspace.id]);
  useEffect(() => { load(); }, [load]);
  const submitEntry = async () => {
    if (!entryForm.customer_id) return toast.error('Grahak select karein');
    const amt = parseFloat(entryForm.amount);
    if (!amt || amt <= 0) return toast.error('Sahi amount daalein');
    // For paise_liye/paise_diye, prompt account selection (but allow skipping if none exist)
    if ((entryForm.entry_type === 'paise_liye' || entryForm.entry_type === 'paise_diye') && accounts.length > 0 && !entryForm.account_id) {
      return toast.error('Cash/Bank account chunein (ya "My Accounts" me naya banayein)');
    }
    setEntryBusy(true);
    try {
      await api.post(`/vyapar/ledger?workspace_id=${workspace.id}`, {
        customer_id: entryForm.customer_id,
        entry_type: entryForm.entry_type,
        amount: amt,
        note: entryForm.note,
        entry_date: entryForm.entry_date,
        account_id: entryForm.account_id || null,
      });
      toast.success(`✅ Entry saved${entryForm.account_id ? ' · account balance synced' : ''} (${entryForm.entry_date})`);
      setEntryForm({ customer_id: entryForm.customer_id, entry_type: 'paise_liye', amount: '', note: '', entry_date: new Date().toISOString().slice(0, 10), account_id: entryForm.account_id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setEntryBusy(false); }
  };
  const addAccount = async () => {
    if (!newAcc.name.trim()) return toast.error('Account name daalein');
    try {
      await api.post('/vyapar/accounts', { workspace_id: workspace.id, name: newAcc.name.trim(), account_type: newAcc.account_type, balance: parseFloat(newAcc.balance) || 0 });
      toast.success('✅ Account created');
      setNewAcc({ name: '', account_type: 'cash', balance: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteAccount = async (id) => {
    if (!window.confirm('Delete this account? (Purani entries safe rahengi)')) return;
    try {
      await api.delete(`/vyapar/accounts/${id}?workspace_id=${workspace.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ============ CASHBOOK PDF EXPORT (per account) ============
  const [cashbook, setCashbook] = useState({ open: false, account: null, range: 'this_month', from: '', to: '', busy: false });
  const openCashbook = (acc) => setCashbook({ open: true, account: acc, range: 'this_month', from: '', to: '', busy: false });
  const closeCashbook = () => setCashbook({ open: false, account: null, range: 'this_month', from: '', to: '', busy: false });
  const runCashbookExport = async (fmt) => {
    const acc = cashbook.account;
    if (!acc) return;
    // Compute date range
    const today = new Date();
    let fromISO = '', toISO = '', label = 'All-Time';
    if (cashbook.range === 'today') {
      fromISO = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
      toISO = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();
      label = today.toLocaleDateString('en-IN');
    } else if (cashbook.range === 'this_month') {
      fromISO = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
      toISO = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59).toISOString();
      label = today.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    } else if (cashbook.range === 'last_month') {
      fromISO = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString();
      toISO = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59).toISOString();
      const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      label = lm.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    } else if (cashbook.range === 'this_year') {
      fromISO = new Date(today.getFullYear(), 0, 1).toISOString();
      toISO = new Date(today.getFullYear(), 11, 31, 23, 59, 59).toISOString();
      label = String(today.getFullYear());
    } else if (cashbook.range === 'custom') {
      if (!cashbook.from || !cashbook.to) return toast.error('From/To dates dalein');
      fromISO = new Date(cashbook.from).toISOString();
      toISO = new Date(cashbook.to + 'T23:59:59').toISOString();
      label = `${cashbook.from} to ${cashbook.to}`;
    }
    setCashbook((f) => ({ ...f, busy: true }));
    try {
      const params = new URLSearchParams({ workspace_id: workspace.id });
      if (fromISO) params.append('from_date', fromISO);
      if (toISO) params.append('to_date', toISO);
      const r = await api.get(`/vyapar/ledger/all?${params}`);
      const all = r.data.entries || [];
      // Filter by this account only (paise_liye/paise_diye that touched this account)
      const entries = all.filter((e) => e.account_id === acc.id);
      if (entries.length === 0) { toast.error('Is period me is account ki koi entry nahi mili'); setCashbook((f) => ({ ...f, busy: false })); return; }
      // Sort chronological + compute running balance (starting from opening balance minus current impact, so we walk forward)
      entries.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      // Best-effort opening: current balance minus net impact of shown entries
      let net = 0;
      entries.forEach((e) => { net += (e.entry_type === 'paise_liye' ? Number(e.amount || 0) : -Number(e.amount || 0)); });
      let running = Number(acc.balance || 0) - net;
      const rows = entries.map((e) => {
        const isCr = e.entry_type === 'paise_liye';
        const amt = Number(e.amount || 0);
        running += isCr ? amt : -amt;
        return {
          date: new Date(e.created_at).toLocaleDateString('en-IN'),
          party: e.customer_name || '-',
          type: e.entry_label || (isCr ? 'Paise Liye' : 'Paise Diye'),
          credit: isCr ? amt : 0,
          debit: isCr ? 0 : amt,
          balance: running,
          note: e.note || '',
        };
      });
      const totalCr = rows.reduce((s, x) => s + x.credit, 0);
      const totalDr = rows.reduce((s, x) => s + x.debit, 0);
      if (fmt === 'csv') {
        const header = 'Date,Party,Type,Credit (In),Debit (Out),Balance,Note\n';
        const csv = header + rows.map((x) => [x.date, `"${x.party}"`, `"${x.type}"`, x.credit || '', x.debit || '', x.balance, `"${(x.note || '').replace(/"/g, '""')}"`].join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `cashbook-${acc.name}-${label}.csv`.replace(/\s+/g, '_'); a.click();
        toast.success('CSV downloaded');
      } else {
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        const doc = new jsPDF();
        doc.setFontSize(16); doc.text('CASHBOOK STATEMENT', 14, 18);
        doc.setFontSize(10); doc.text(`${workspace.business_name}`, 14, 25);
        doc.setFontSize(9); doc.setTextColor(90,90,90);
        doc.text(`Account: ${acc.name} (${acc.account_type})`, 14, 32);
        doc.text(`Period: ${label}`, 14, 38);
        doc.text(`Closing Balance: Rs.${Number(acc.balance || 0).toLocaleString('en-IN')}`, 14, 44);
        doc.setTextColor(0,0,0);
        autoTable(doc, {
          startY: 50,
          head: [['Date','Party','Type','Credit (In)','Debit (Out)','Balance','Note']],
          body: rows.map((x) => [x.date, x.party, x.type, x.credit ? x.credit.toLocaleString('en-IN') : '-', x.debit ? x.debit.toLocaleString('en-IN') : '-', x.balance.toLocaleString('en-IN'), x.note]),
          foot: [['', '', 'TOTAL', totalCr.toLocaleString('en-IN'), totalDr.toLocaleString('en-IN'), Number(acc.balance || 0).toLocaleString('en-IN'), '']],
          styles: { fontSize: 8 },
          headStyles: { fillColor: [16, 185, 129] },
          footStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
          columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' } },
        });
        const finalY = doc.lastAutoTable.finalY || 60;
        doc.setFontSize(8); doc.setTextColor(120,120,120);
        doc.text('Computer-generated cashbook from Anant Vyapar. Sirf reconciliation ke liye.', 14, finalY + 8);
        doc.save(`cashbook-${acc.name}-${label}.pdf`.replace(/\s+/g, '_'));
        toast.success('PDF downloaded');
      }
      closeCashbook();
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); setCashbook((f) => ({ ...f, busy: false })); }
  };
  const ENTRY_TYPES = [
    { id: 'paise_liye',   label: 'Paise Liye',    hint: 'Grahak se paisa mila',       icon: '💰', cls: 'bg-emerald-500/20 border-emerald-400/60' },
    { id: 'paise_diye',   label: 'Paise Diye',    hint: 'Grahak ko paisa diya',       icon: '💸', cls: 'bg-rose-500/20 border-rose-400/60' },
    { id: 'maal_becha',   label: 'Maal Becha',    hint: 'Grahak ko maal diya (udhari)', icon: '📤', cls: 'bg-sky-500/20 border-sky-400/60' },
    { id: 'maal_kharida', label: 'Maal Kharida',  hint: 'Grahak/supplier se maal liya', icon: '📥', cls: 'bg-amber-500/20 border-amber-400/60' },
  ];
  const sendReminder = (row) => {
    const num = String(row.whatsapp || row.mobile || '').replace(/\D/g, '');
    const bal = row.balance || row.balance === 0 ? row.balance : (row.balance || 0);
    const dueTxt = row.due_date ? `\n📅 Due Date: ${new Date(row.due_date).toLocaleDateString('en-IN')}` : '';
    const daysTxt = typeof row.days_left === 'number'
      ? (row.days_left < 0 ? `\n⚠️ *${Math.abs(row.days_left)} din overdue*` : row.days_left === 0 ? `\n⏰ *Aaj hi due hai*` : `\n⏰ Baaki: *${row.days_left} din*`)
      : '';
    const text = `Namaste ${row.customer_name || row.name},\n\n*${workspace.business_name}* se udhari yaad dilana chah rahe hain.\n\n💰 Baki: ₹${bal}${dueTxt}${daysTxt}\n\nJab convenient ho tab de dijiyega. Dhanyawad 🙏\n\n_Sent via Anant Vyapar_`;
    const url = `https://wa.me/${num.length === 10 ? '91' + num : num}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // ============ Per-Grahak Khata Export (PDF / CSV) ============
  const buildLedgerRows = (entries) => {
    let running = 0;
    return entries.map((e) => {
      // From vyapar's perspective: debit means grahak owes us (+), credit means grahak paid/we owe (-)
      const amt = Number(e.amount || 0);
      if (e.tx_type === 'debit') running += amt; else running -= amt;
      return {
        date: new Date(e.created_at).toLocaleDateString('en-IN'),
        label: e.entry_label || (e.tx_type === 'debit' ? 'Debit' : 'Credit'),
        icon: e.entry_icon || '',
        debit: e.tx_type === 'debit' ? amt : 0,
        credit: e.tx_type === 'credit' ? amt : 0,
        balance: running,
        note: e.note || '',
      };
    });
  };
  const exportGrahakLedger = async (row, fmt) => {
    try {
      const r = await api.get(`/vyapar/customers/${row.customer_id}`);
      const c = r.data.customer;
      const rows = buildLedgerRows(r.data.ledger_entries || []);
      const finalBal = r.data.ledger_balance || 0;
      const ws = workspace;
      const businessLine = `${ws.business_name}${ws.owner_name ? ' · ' + ws.owner_name : ''}`;
      const contactLine = `${ws.mobile || ''}${ws.gstin ? ' · GSTIN: ' + ws.gstin : ''}${ws.address ? '\n' + ws.address : ''}`;
      if (fmt === 'csv') {
        const header = ['Date','Type','Debit (₹)','Credit (₹)','Running Balance (₹)','Note'];
        const csvRows = rows.map((x) => [x.date, `${x.icon} ${x.label}`, x.debit || '', x.credit || '', x.balance, x.note]);
        const meta = [[businessLine], [contactLine.replace('\n',' | ')], [`Khata: ${c.name} · ${c.mobile || ''}`], [`Net Balance: ₹${finalBal} (${finalBal > 0 ? 'Lena Baki' : finalBal < 0 ? 'Dena Baki' : 'Settled'})`], []];
        const csv = [...meta, header, ...csvRows].map((r2) => r2.map((v) => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `khata-${c.name}-${ws.business_name}.csv`; a.click();
        toast.success('CSV downloaded');
      } else {
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        const doc = new jsPDF();
        // Header
        doc.setFontSize(16); doc.setFont('helvetica', 'bold');
        doc.text(businessLine, 14, 15);
        doc.setFontSize(9); doc.setFont('helvetica', 'normal');
        doc.text(contactLine, 14, 22);
        doc.setFontSize(13); doc.setFont('helvetica', 'bold');
        doc.text(`Khata Bahi — ${c.name}`, 14, 40);
        doc.setFontSize(9); doc.setFont('helvetica', 'normal');
        doc.text(`Mobile: ${c.mobile || '-'}   |   Generated: ${new Date().toLocaleString('en-IN')}`, 14, 46);
        // Net balance badge
        const balLabel = finalBal > 0 ? 'Lena Baki (Grahak se lena)' : finalBal < 0 ? 'Dena Baki (Grahak ko dena)' : 'Settled';
        doc.setFillColor(finalBal > 0 ? 239 : finalBal < 0 ? 16 : 100, finalBal > 0 ? 68 : finalBal < 0 ? 185 : 116, finalBal > 0 ? 68 : finalBal < 0 ? 129 : 139);
        doc.rect(14, 50, 182, 8, 'F');
        doc.setTextColor(255,255,255); doc.setFont('helvetica','bold');
        doc.text(`Net Balance: Rs. ${Math.abs(finalBal).toLocaleString('en-IN')}  (${balLabel})`, 16, 56);
        doc.setTextColor(0,0,0);
        autoTable(doc, {
          startY: 62,
          head: [['Date','Type','Debit (Rs.)','Credit (Rs.)','Balance (Rs.)','Note']],
          body: rows.map((x) => [x.date, x.label, x.debit ? x.debit.toLocaleString('en-IN') : '-', x.credit ? x.credit.toLocaleString('en-IN') : '-', x.balance.toLocaleString('en-IN'), x.note || '']),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [217, 70, 239] },
          columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right', fontStyle: 'bold' } },
        });
        // Footer
        const finalY = doc.lastAutoTable.finalY || 70;
        doc.setFontSize(8); doc.setTextColor(120,120,120);
        doc.text('This is a computer-generated ledger from Anant Vyapar. For reconciliation only.', 14, finalY + 8);
        doc.save(`khata-${c.name}-${ws.business_name}.pdf`);
        toast.success('PDF downloaded');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
  };
  // ============ WHOLE BUSINESS EXPORT (combined khata bahi) ============
  const [wbExport, setWbExport] = useState({ open: false, range: 'all', from: '', to: '', busy: false });
  const openWholeExport = () => setWbExport({ open: true, range: 'all', from: '', to: '', busy: false });
  const runWholeExport = async (fmt) => {
    // Compute date range
    const today = new Date();
    let fromISO = '', toISO = '';
    if (wbExport.range === 'this_month') {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      fromISO = d.toISOString();
      toISO = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59).toISOString();
    } else if (wbExport.range === 'last_month') {
      const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      fromISO = d.toISOString();
      toISO = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59).toISOString();
    } else if (wbExport.range === 'last_3') {
      fromISO = new Date(today.getFullYear(), today.getMonth() - 3, 1).toISOString();
      toISO = today.toISOString();
    } else if (wbExport.range === 'custom') {
      if (!wbExport.from || !wbExport.to) return toast.error('From/To dates dalein');
      fromISO = new Date(wbExport.from).toISOString();
      toISO = new Date(wbExport.to + 'T23:59:59').toISOString();
    }
    setWbExport((f) => ({ ...f, busy: true }));
    try {
      const params = new URLSearchParams({ workspace_id: workspace.id });
      if (fromISO) params.append('from_date', fromISO);
      if (toISO) params.append('to_date', toISO);
      const r = await api.get(`/vyapar/ledger/all?${params}`);
      const entries = r.data.entries || [];
      if (entries.length === 0) { toast.error('Is date range mein koi entry nahi mili'); setWbExport((f) => ({ ...f, busy: false })); return; }
      // Group by customer + compute running balance
      const groups = {};
      entries.forEach((e) => {
        const cid = e.customer_id;
        if (!groups[cid]) groups[cid] = { name: e.customer_name, mobile: e.customer_mobile, entries: [], running: 0 };
        const amt = Number(e.amount || 0);
        if (e.tx_type === 'debit') groups[cid].running += amt; else groups[cid].running -= amt;
        groups[cid].entries.push({
          date: new Date(e.created_at).toLocaleDateString('en-IN'),
          type: e.entry_label || (e.tx_type === 'debit' ? 'Debit' : 'Credit'),
          debit: e.tx_type === 'debit' ? amt : 0,
          credit: e.tx_type === 'credit' ? amt : 0,
          balance: groups[cid].running,
          note: e.note || '',
        });
      });
      const groupList = Object.values(groups).sort((a, b) => Math.abs(b.running) - Math.abs(a.running));
      const grandDue = groupList.filter((g) => g.running > 0).reduce((s, g) => s + g.running, 0);
      const grandAdv = groupList.filter((g) => g.running < 0).reduce((s, g) => s + Math.abs(g.running), 0);
      const ws = workspace;
      const rangeLabel = wbExport.range === 'all' ? 'All Time' : wbExport.range === 'this_month' ? 'This Month' : wbExport.range === 'last_month' ? 'Last Month' : wbExport.range === 'last_3' ? 'Last 3 Months' : `${wbExport.from} to ${wbExport.to}`;

      if (fmt === 'csv') {
        const meta = [
          [ws.business_name, ws.owner_name || ''],
          [`Mobile: ${ws.mobile || '-'}`, `GSTIN: ${ws.gstin || '-'}`],
          [`Address: ${ws.address || '-'}`],
          [`Period: ${rangeLabel}`, `Generated: ${new Date().toLocaleString('en-IN')}`],
          [`Grand Lena Baki: Rs.${grandDue}`, `Grand Dena Baki: Rs.${grandAdv}`],
          [],
        ];
        const header = ['Grahak','Mobile','Date','Type','Debit (Rs.)','Credit (Rs.)','Running Balance (Rs.)','Note'];
        const body = [];
        groupList.forEach((g) => {
          g.entries.forEach((x) => body.push([g.name, g.mobile, x.date, x.type, x.debit || '', x.credit || '', x.balance, x.note]));
          body.push([g.name, g.mobile, '', 'GRAHAK NET', '', '', g.running, g.running > 0 ? 'Lena Baki' : g.running < 0 ? 'Dena Baki' : 'Settled']);
          body.push([]);
        });
        const csv = [...meta, header, ...body].map((row) => row.map((v) => `"${String(v ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `khata-bahi-${ws.business_name}-${wbExport.range}.csv`; a.click();
        toast.success('Combined CSV downloaded');
      } else {
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        const doc = new jsPDF();
        // Cover / header
        doc.setFontSize(18); doc.setFont('helvetica','bold');
        doc.text(ws.business_name, 14, 18);
        doc.setFontSize(10); doc.setFont('helvetica','normal');
        doc.text(`${ws.owner_name || ''}`, 14, 24);
        doc.text(`Mobile: ${ws.mobile || '-'}   |   GSTIN: ${ws.gstin || '-'}`, 14, 30);
        if (ws.address) doc.text(ws.address, 14, 36);
        doc.setFontSize(14); doc.setFont('helvetica','bold');
        doc.text('Khata Bahi — Combined Report', 14, 48);
        doc.setFontSize(9); doc.setFont('helvetica','normal');
        doc.text(`Period: ${rangeLabel}   |   Generated: ${new Date().toLocaleString('en-IN')}   |   Total Grahaks: ${groupList.length}`, 14, 54);
        // Grand totals
        doc.setFillColor(239, 68, 68); doc.rect(14, 58, 88, 10, 'F');
        doc.setFillColor(16, 185, 129); doc.rect(108, 58, 88, 10, 'F');
        doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(10);
        doc.text(`Grand Lena Baki: Rs. ${grandDue.toLocaleString('en-IN')}`, 18, 65);
        doc.text(`Grand Dena Baki: Rs. ${grandAdv.toLocaleString('en-IN')}`, 112, 65);
        doc.setTextColor(0,0,0);
        let cursorY = 76;
        // Per-grahak sections
        groupList.forEach((g, idx) => {
          if (cursorY > 260) { doc.addPage(); cursorY = 20; }
          doc.setFontSize(11); doc.setFont('helvetica','bold');
          doc.text(`${idx + 1}. ${g.name}${g.mobile ? ' · ' + g.mobile : ''}`, 14, cursorY);
          doc.setFontSize(9); doc.setFont('helvetica','normal');
          const balLabel = g.running > 0 ? `Lena Baki: Rs. ${g.running.toLocaleString('en-IN')}` : g.running < 0 ? `Dena Baki: Rs. ${Math.abs(g.running).toLocaleString('en-IN')}` : 'Settled';
          doc.setTextColor(g.running > 0 ? 220 : g.running < 0 ? 16 : 100, g.running > 0 ? 38 : g.running < 0 ? 129 : 116, g.running > 0 ? 38 : g.running < 0 ? 62 : 139);
          doc.text(balLabel, 150, cursorY);
          doc.setTextColor(0,0,0);
          autoTable(doc, {
            startY: cursorY + 3,
            head: [['Date','Type','Debit','Credit','Balance','Note']],
            body: g.entries.map((x) => [x.date, x.type, x.debit ? x.debit.toLocaleString('en-IN') : '-', x.credit ? x.credit.toLocaleString('en-IN') : '-', x.balance.toLocaleString('en-IN'), x.note || '']),
            styles: { fontSize: 7.5 },
            headStyles: { fillColor: [148, 163, 184], textColor: 0 },
            columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 14, right: 14 },
          });
          cursorY = (doc.lastAutoTable.finalY || cursorY) + 8;
        });
        doc.setFontSize(8); doc.setTextColor(120,120,120);
        doc.text('Anant Vyapar — Combined Khata Bahi. For CA/Accountant reconciliation.', 14, 285);
        doc.save(`khata-bahi-${ws.business_name}-${wbExport.range}.pdf`);
        toast.success('Combined PDF downloaded');
      }
      setWbExport({ open: false, range: 'all', from: '', to: '', busy: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
      setWbExport((f) => ({ ...f, busy: false }));
    }
  };

  return (
    <div className="space-y-3" data-testid="vyapar-ledger-tab">
      {/* Khata Book Totals */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3" data-testid="khata-lena-baki"><div className="text-xs text-red-300 uppercase font-bold">Lena Baki (Grahak se)</div><div className="text-2xl font-extrabold text-red-200">₹{summary.total_due?.toLocaleString('en-IN') || 0}</div><div className="text-[10px] text-red-300/70">Aap ko grahak se paisa milna hai</div></div>
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3" data-testid="khata-dena-baki"><div className="text-xs text-emerald-300 uppercase font-bold">Dena Baki (Aap ko)</div><div className="text-2xl font-extrabold text-emerald-200">₹{summary.total_advance?.toLocaleString('en-IN') || 0}</div><div className="text-[10px] text-emerald-300/70">Aap ko supplier/grahak ko dena hai</div></div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={() => setBulkOpen(true)} disabled={customers.length === 0} data-testid="ledger-bulk-msg-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-blue-500 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40">📢 Bulk Message</button>
        <button onClick={openWholeExport} data-testid="wb-export-open" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white text-xs font-bold flex items-center gap-1">🏢 Whole Business Export (PDF/CSV)</button>
      </div>
      <BulkMessenger open={bulkOpen} onClose={() => setBulkOpen(false)} recipients={customers.map((c) => ({ id: c.id, name: c.name, mobile: c.whatsapp || c.mobile }))} defaultTemplate={`Namaste {name},\n\n${workspace.business_name} ki taraf se yaad dilana chahte hain.\n\nDhanyawad 🙏\n_${workspace.business_name}_`} label="Grahakon ko Bulk Message" />

      {/* Whole Business Export Modal */}
      {wbExport.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !wbExport.busy && setWbExport((f) => ({ ...f, open: false }))} data-testid="wb-export-modal">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-fuchsia-400/30 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-extrabold">🏢 Whole Business Khata Export</h3>
              <button onClick={() => setWbExport((f) => ({ ...f, open: false }))} className="p-1 rounded hover:bg-white/10">✕</button>
            </div>
            <label className="text-[11px] uppercase font-bold text-slate-400">Period / Month Filter</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {[{ id: 'all', label: 'All Time' },{ id: 'this_month', label: 'This Month' },{ id: 'last_month', label: 'Last Month' },{ id: 'last_3', label: 'Last 3 Months' },{ id: 'custom', label: 'Custom Range' }].map((r) => (
                <button key={r.id} onClick={() => setWbExport((f) => ({ ...f, range: r.id }))} data-testid={`wb-range-${r.id}`} className={`py-1.5 rounded-lg text-xs font-bold ${wbExport.range === r.id ? 'bg-fuchsia-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}>{r.label}</button>
              ))}
            </div>
            {wbExport.range === 'custom' && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div><label className="text-[10px] text-slate-400">From</label><input type="date" value={wbExport.from} onChange={(e) => setWbExport((f) => ({ ...f, from: e.target.value }))} data-testid="wb-from" className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" /></div>
                <div><label className="text-[10px] text-slate-400">To</label><input type="date" value={wbExport.to} onChange={(e) => setWbExport((f) => ({ ...f, to: e.target.value }))} data-testid="wb-to" className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" /></div>
              </div>
            )}
            <div className="mt-4 rounded-lg bg-slate-800/50 border border-white/5 p-3 text-[11px] text-slate-300">
              📄 Export mein aayega: business branding + har grahak ka full ledger with running balance + Grand Lena/Dena Baki totals. CA / accountant ko dikhane ke liye ready.
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => runWholeExport('pdf')} disabled={wbExport.busy} data-testid="wb-pdf-btn" className="py-2 rounded-lg bg-red-500 text-white font-bold text-sm disabled:opacity-60">{wbExport.busy ? '…' : '📄 PDF'}</button>
              <button onClick={() => runWholeExport('csv')} disabled={wbExport.busy} data-testid="wb-csv-btn" className="py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm disabled:opacity-60">{wbExport.busy ? '…' : '📊 CSV (Excel)'}</button>
            </div>
          </div>
        </div>
      )}


      {/* Quick Khata Entry */}
      <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-slate-900/60 to-fuchsia-950/30 p-4" data-testid="khata-entry-form">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-fuchsia-300" /><h3 className="text-sm font-bold">Khata Bahi — Quick Entry (₹)</h3></div>
          <button type="button" onClick={() => setShowAccMgr((v) => !v)} data-testid="vyapar-accounts-toggle" className="text-[10px] px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 font-bold">
            🏦 My Accounts ({accounts.length}) {showAccMgr ? '▲' : '▼'}
          </button>
        </div>

        {showAccMgr && (
          <div className="mb-3 p-3 rounded-xl border border-emerald-400/30 bg-emerald-500/5" data-testid="vyapar-accounts-manager">
            <div className="text-[11px] font-bold text-emerald-200 mb-2">🏦 Cash / Bank / UPI Accounts — Auto-sync on Paise Liye/Diye</div>
            {accounts.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                {accounts.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-2 py-1.5">
                    <div className="min-w-0">
                      <div className="text-xs font-bold truncate">{a.name}</div>
                      <div className="text-[10px] text-slate-400 capitalize">{a.account_type} · <span className="text-emerald-300 font-mono">₹{Number(a.balance || 0).toLocaleString('en-IN')}</span></div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openCashbook(a)} data-testid={`vyapar-acc-pdf-${a.id}`} className="text-[10px] px-1.5 py-1 rounded bg-emerald-500/25 text-emerald-200 font-bold hover:bg-emerald-500/40" title="Download Cashbook Statement (PDF/CSV)">📄 PDF</button>
                      <button onClick={() => deleteAccount(a.id)} className="text-red-300 p-1" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <input placeholder="e.g. Cash / HDFC / GPay" value={newAcc.name} onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })} data-testid="vyapar-newacc-name" className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
              <select value={newAcc.account_type} onChange={(e) => setNewAcc({ ...newAcc, account_type: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs">
                <option value="cash" className="bg-slate-900">💵 Cash</option>
                <option value="bank" className="bg-slate-900">🏦 Bank</option>
                <option value="upi" className="bg-slate-900">📱 UPI/Wallet</option>
                <option value="other" className="bg-slate-900">📦 Other</option>
              </select>
              <input type="number" placeholder="Opening ₹" value={newAcc.balance} onChange={(e) => setNewAcc({ ...newAcc, balance: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
              <button onClick={addAccount} data-testid="vyapar-newacc-save" className="py-1.5 rounded bg-emerald-500 text-white text-xs font-bold">➕ Add Account</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {ENTRY_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setEntryForm((f) => ({ ...f, entry_type: t.id }))}
              data-testid={`khata-type-${t.id}`}
              className={`p-2 rounded-xl border text-left transition ${entryForm.entry_type === t.id ? `${t.cls} shadow-lg` : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
              <div className="text-lg">{t.icon}</div>
              <div className="text-xs font-bold">{t.label}</div>
              <div className="text-[9px] text-slate-400 leading-tight">{t.hint}</div>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
          <select value={entryForm.customer_id} onChange={(e) => setEntryForm((f) => ({ ...f, customer_id: e.target.value }))} data-testid="khata-customer-select" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm sm:col-span-2">
            <option value="">-- Grahak chunein --</option>
            {customers.map((c) => <option key={c.id} value={c.id} className="bg-slate-900">{c.name} {c.mobile ? `· ${c.mobile}` : ''}</option>)}
          </select>
          <select
            value={entryForm.account_id}
            onChange={(e) => setEntryForm((f) => ({ ...f, account_id: e.target.value }))}
            data-testid="khata-account-select"
            className="bg-white/5 border border-emerald-400/40 rounded-lg px-2 py-2 text-sm font-semibold"
            title="Paise kis apne account me/se? (Cash/Bank/UPI)"
          >
            <option value="" className="bg-slate-900">
              {entryForm.entry_type === 'paise_liye' ? '💰 Kis account me?' : entryForm.entry_type === 'paise_diye' ? '💸 Kis account se?' : '🏦 Account (optional)'}
            </option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id} className="bg-slate-900">{a.name} · ₹{Number(a.balance || 0).toLocaleString('en-IN')}</option>
            ))}
          </select>
          <input type="date" value={entryForm.entry_date} onChange={(e) => setEntryForm((f) => ({ ...f, entry_date: e.target.value }))} data-testid="khata-date" title="Transaction date (backdate allowed)" className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm" />
          <div className="relative">
            <span className="absolute left-2.5 top-2 text-slate-400 text-sm">₹</span>
            <input type="number" placeholder="Amount" value={entryForm.amount} onChange={(e) => setEntryForm((f) => ({ ...f, amount: e.target.value }))} data-testid="khata-amount" className="w-full pl-6 pr-2 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" />
          </div>
          <button onClick={submitEntry} disabled={entryBusy} data-testid="khata-submit" className="py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-sm font-bold disabled:opacity-60">
            {entryBusy ? 'Saving…' : 'Add Entry'}
          </button>
        </div>
        <input placeholder="Note (optional, e.g. 'Bill #45 ka payment')" value={entryForm.note} onChange={(e) => setEntryForm((f) => ({ ...f, note: e.target.value }))} data-testid="khata-note" className="w-full mt-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs" />
      </div>

      {/* Auto-cron Reminders */}
      {(reminders.overdue.length > 0 || reminders.upcoming.length > 0) && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3" data-testid="udhari-reminders">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-300" />
            <div className="font-bold text-sm">🔔 Udhari Reminders — Auto WhatsApp Alerts</div>
          </div>
          {reminders.overdue.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] uppercase font-bold text-red-300 mb-1">🚨 Overdue ({reminders.overdue.length})</div>
              <div className="space-y-1">
                {reminders.overdue.slice(0, 5).map((r) => (
                  <div key={r.customer_id} className="flex items-center justify-between bg-red-500/10 rounded-lg px-2 py-1.5 text-xs">
                    <div className="min-w-0"><b>{r.name}</b> <span className="text-red-300">· ₹{r.balance}</span> <span className="text-red-400 ml-1">({Math.abs(r.days_left)}d overdue)</span></div>
                    <button onClick={() => sendReminder(r)} data-testid={`rem-overdue-${r.customer_id}`} className="text-[10px] px-2 py-1 rounded bg-emerald-500 text-white font-bold ml-2">📤 WhatsApp</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {reminders.upcoming.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold text-amber-300 mb-1">⏰ Next 3 days ({reminders.upcoming.length})</div>
              <div className="space-y-1">
                {reminders.upcoming.slice(0, 5).map((r) => (
                  <div key={r.customer_id} className="flex items-center justify-between bg-amber-500/10 rounded-lg px-2 py-1.5 text-xs">
                    <div className="min-w-0"><b>{r.name}</b> <span className="text-amber-200">· ₹{r.balance}</span> <span className="text-slate-400 ml-1">({r.days_left === 0 ? 'aaj' : `${r.days_left}d baaki`})</span></div>
                    <button onClick={() => sendReminder(r)} data-testid={`rem-upcoming-${r.customer_id}`} className="text-[10px] px-2 py-1 rounded bg-emerald-500 text-white font-bold ml-2">📤 WhatsApp</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-2 italic">Ye page open karte hi automatic scan hota hai. Customer form me "Credit Due Date" set karein.</div>
        </div>
      )}
      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">Grahak</th><th className="text-right px-3 py-2">Balance</th><th className="px-3 py-2">Last Activity</th><th className="px-3 py-2">Action</th></tr></thead>
          <tbody>
            {summary.rows.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500 text-xs">Koi khata entry nahi. Upar se Quick Entry karke shuru karein.</td></tr>}
            {summary.rows.map((r) => (
              <tr key={r.customer_id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-2"><div className="font-bold">{r.customer_name}</div><div className="text-xs text-slate-400">{r.mobile}</div></td>
                <td className={`px-3 py-2 text-right font-bold ${r.balance > 0 ? 'text-red-300' : r.balance < 0 ? 'text-emerald-300' : 'text-slate-400'}`}>
                  ₹{Math.abs(r.balance).toLocaleString('en-IN')}
                  <div className="text-[9px] font-normal">{r.balance > 0 ? 'Lena baki' : r.balance < 0 ? 'Dena baki' : 'Settled'}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-400">{new Date(r.last_at).toLocaleDateString('en-IN')}</td>
                <td className="px-3 py-2 text-center">
                  <div className="flex gap-1 justify-center items-center flex-wrap">
                    {r.balance > 0 && <button onClick={() => sendReminder(r)} data-testid={`ledger-remind-${r.customer_id}`} className="text-emerald-300 text-xs font-bold" title="WhatsApp reminder">📤</button>}
                    <button
                      onClick={() => {
                        const num = String(r.whatsapp || r.mobile || '').replace(/\D/g, '');
                        const bal = Number(r.balance || 0);
                        const dir = bal > 0 ? 'Aap ka *Lena Baki* hai (aapne diya hai)' : bal < 0 ? 'Aap ka *Dena Baki* hai (humse liya hai)' : 'Aap ka khata *Settled* hai';
                        const msg = `📒 *Khata Statement* — ${workspace.business_name}\n\n` +
                          `Namaste ${r.customer_name || r.name},\n\n` +
                          `📌 Naam: *${r.customer_name || r.name}*\n` +
                          `💰 Net Balance: *₹${Math.abs(bal).toLocaleString('en-IN')}*\n` +
                          `📊 Status: ${dir}\n` +
                          `📅 Last Entry: ${new Date(r.last_at).toLocaleDateString('en-IN')}\n\n` +
                          `Full khata bahi PDF humne save ki hai. Koi bhi query ho toh reply kar dena.\n\n` +
                          `Dhanyawad 🙏\n_Anant Smart Vyapar_`;
                        const url = num ? `https://wa.me/${num.length === 10 ? '91' + num : num}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
                        window.open(url, '_blank');
                      }}
                      data-testid={`ledger-wa-${r.customer_id}`}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 font-bold hover:bg-green-500/30"
                      title="Khata Statement WhatsApp par bhejein">WA</button>
                    <button onClick={() => exportGrahakLedger(r, 'pdf')} data-testid={`ledger-pdf-${r.customer_id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 font-bold hover:bg-red-500/30" title="Khata PDF download">PDF</button>
                    <button onClick={() => exportGrahakLedger(r, 'csv')} data-testid={`ledger-csv-${r.customer_id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold hover:bg-emerald-500/30" title="Excel/CSV download">CSV</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cashbook Statement Modal */}
      {cashbook.open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={closeCashbook}>
          <div className="bg-slate-900 rounded-2xl border border-emerald-400/40 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="vyapar-cashbook-modal">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2">📄 Cashbook Statement</h3>
              <button onClick={closeCashbook}><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-300 mb-3">
              <b className="text-emerald-300">{cashbook.account?.name}</b> ({cashbook.account?.account_type}) · Current Balance: <span className="font-mono text-emerald-300">₹{Number(cashbook.account?.balance || 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Period</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { id: 'today', label: 'Today' },
                { id: 'this_month', label: 'This Month' },
                { id: 'last_month', label: 'Last Month' },
                { id: 'this_year', label: 'This Year' },
                { id: 'custom', label: 'Custom' },
              ].map((r) => (
                <button
                  key={r.id}
                  onClick={() => setCashbook((f) => ({ ...f, range: r.id }))}
                  data-testid={`cashbook-range-${r.id}`}
                  className={`text-xs px-2 py-1.5 rounded-lg border ${cashbook.range === r.id ? 'bg-emerald-500/20 border-emerald-400 text-emerald-100 font-bold' : 'bg-white/5 border-white/10 text-slate-300'}`}
                >{r.label}</button>
              ))}
            </div>
            {cashbook.range === 'custom' && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400">From</label>
                  <input type="date" value={cashbook.from} onChange={(e) => setCashbook((f) => ({ ...f, from: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-400">To</label>
                  <input type="date" value={cashbook.to} onChange={(e) => setCashbook((f) => ({ ...f, to: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
                </div>
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <button onClick={() => runCashbookExport('pdf')} disabled={cashbook.busy} data-testid="cashbook-download-pdf" className="flex-1 py-2 rounded-lg bg-gradient-to-r from-red-500 to-rose-500 text-white text-sm font-bold disabled:opacity-60">{cashbook.busy ? 'Preparing…' : '📄 Download PDF'}</button>
              <button onClick={() => runCashbookExport('csv')} disabled={cashbook.busy} data-testid="cashbook-download-csv" className="flex-1 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold disabled:opacity-60">📊 CSV</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const MagicQRTab = ({ workspace }) => {
  const [info, setInfo] = useState(null);
  const [widgetStats, setWidgetStats] = useState(null);
  const [showEmbed, setShowEmbed] = useState(false);
  useEffect(() => { api.get(`/vyapar/magic-qr/info?workspace_id=${workspace.id}`).then((r) => setInfo(r.data)); }, [workspace.id]);
  useEffect(() => {
    if (info?.token) api.get(`/safety-qr/widget-view/stats/${info.token}`).then((r) => setWidgetStats(r.data)).catch(() => {});
  }, [info?.token]);
  const publicUrl = info ? `${window.location.origin}/vyapar/qr/${info.token}` : '';
  const qrImg = info ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(publicUrl)}` : '';
  const tier = info?.tier || 'unverified';
  const count = info?.customer_count || 0;
  const next = info?.next_tier;
  const score = info?.trust_score || 1.0;
  // Progress % towards next tier (from current tier lower bound)
  const tierLowers = { unverified: 0, verified: 50, trusted: 200, elite: 500 };
  const currLow = tierLowers[tier] || 0;
  const nextThreshold = next?.threshold || 500;
  const pct = tier === 'elite' ? 100 : Math.min(100, Math.round(((count - currLow) / (nextThreshold - currLow)) * 100));
  const tierStyles = {
    elite:      { chipBg: 'bg-amber-500/10',   chipBd: 'border-amber-400/50',   text: 'text-amber-200',   iconBg: 'bg-amber-500/20',   iconTxt: 'text-amber-300',   bar: 'from-amber-400 to-yellow-300',  label: 'Elite by Anant Network',   emoji: '🏆' },
    trusted:    { chipBg: 'bg-blue-500/10',    chipBd: 'border-blue-400/50',    text: 'text-blue-200',    iconBg: 'bg-blue-500/20',    iconTxt: 'text-blue-300',    bar: 'from-blue-400 to-cyan-300',    label: 'Trusted by Anant Network', emoji: '💎' },
    verified:   { chipBg: 'bg-emerald-500/10', chipBd: 'border-emerald-400/50', text: 'text-emerald-200', iconBg: 'bg-emerald-500/20', iconTxt: 'text-emerald-300', bar: 'from-emerald-400 to-green-300', label: 'Verified by Anant Network', emoji: '✅' },
    unverified: { chipBg: 'bg-amber-500/10',   chipBd: 'border-amber-400/30',   text: 'text-amber-100',   iconBg: 'bg-amber-500/20',   iconTxt: 'text-amber-300',   bar: 'from-amber-400 to-emerald-400', label: 'Unverified',                emoji: '🔓' },
  };
  const s = tierStyles[tier];
  return (
    <div className="space-y-3" data-testid="vyapar-qr-tab">
      {/* Anant Trust Score Strip */}
      {info && (
        <div className={`rounded-2xl border p-3 ${s.chipBg} ${s.chipBd}`} data-testid="vyapar-verified-strip">
          <div className="flex items-center gap-3">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${s.iconBg}`}>
              <Shield className={`w-7 h-7 ${s.iconTxt}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-bold text-sm ${s.text} flex items-center gap-1.5`}>
                {s.emoji} {s.label}
                <span className="text-[10px] font-mono bg-white/10 px-1.5 py-0.5 rounded-full">Trust Score: {score.toFixed(1)} ⭐</span>
              </div>
              <div className="text-[11px] text-slate-300 mt-0.5">
                {count} trusted customers via Magic QR
                {next && <> · <span className="text-slate-400">{next.customers_needed} aur → <b className="text-white">{next.tier}</b> badge</span></>}
                {!next && tier === 'elite' && <span className="text-amber-300 ml-1">🏆 Max tier — Aap Paratwada ke top shops mein hain!</span>}
              </div>
              {next && (
                <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="tier-progress">
                  <div className={`h-full bg-gradient-to-r ${s.bar} transition-all`} style={{ width: `${pct}%` }} />
                </div>
              )}
            </div>
          </div>
          {/* Tier ladder */}
          <div className="mt-3 grid grid-cols-3 gap-1.5 text-[10px]">
            <div className={`rounded-lg border px-2 py-1.5 text-center ${tier === 'verified' || tier === 'trusted' || tier === 'elite' ? 'border-emerald-400/60 bg-emerald-500/10' : 'border-white/10 opacity-60'}`}>
              ✅ <b>Verified</b><div className="text-slate-400">50+</div>
            </div>
            <div className={`rounded-lg border px-2 py-1.5 text-center ${tier === 'trusted' || tier === 'elite' ? 'border-blue-400/60 bg-blue-500/10' : 'border-white/10 opacity-60'}`}>
              💎 <b>Trusted</b><div className="text-slate-400">200+</div>
            </div>
            <div className={`rounded-lg border px-2 py-1.5 text-center ${tier === 'elite' ? 'border-amber-400/60 bg-amber-500/10' : 'border-white/10 opacity-60'}`}>
              🏆 <b>Elite</b><div className="text-slate-400">500+</div>
            </div>
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-fuchsia-400/30 bg-slate-900/60 p-6 text-center">
        <h3 className="text-xl font-bold mb-2">Aapka Magic QR</h3>
        <p className="text-sm text-slate-400 mb-4">Isko print karke counter par lagayein. Grahak scan karke apna naam-number khud daal denge.</p>
        {qrImg && <img src={qrImg} alt="Magic QR" className="mx-auto rounded-xl border-4 border-white bg-white" data-testid="qr-image" />}
        <div className="mt-3 text-xs text-slate-400 break-all">{publicUrl}</div>
        <div className="mt-3 flex gap-2 justify-center flex-wrap">
          <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }} data-testid="qr-copy" className="px-3 py-1.5 rounded-lg bg-white/10 text-xs font-bold">Copy Link</button>
          <a href={qrImg} download={`${workspace.business_name}-magic-qr.png`} data-testid="qr-download" className="px-3 py-1.5 rounded-lg bg-fuchsia-500 text-white text-xs font-bold">Download QR</a>
          <button onClick={() => window.print()} className="px-3 py-1.5 rounded-lg bg-white/10 text-xs font-bold">Print</button>
        </div>
      </div>
      <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3 text-xs text-slate-300">
        <b>🚀 Anant Trust Score kaise badhaayein?</b>
        <ol className="list-decimal ml-4 mt-1 space-y-0.5">
          <li>QR ko print karke aapki dukan ke counter par laga dein — grahak khud add hoga.</li>
          <li>Jitne zyada Magic-QR customers, utna high Trust Score aur badge upgrade.</li>
          <li><b className="text-emerald-300">50+</b> = Verified (green) · <b className="text-blue-300">200+</b> = Trusted (blue) · <b className="text-amber-300">500+</b> = Elite (gold) 🏆</li>
          <li>Public scan page par badge + Trust Score sab customers ko dikhega — Google Business jaisa social proof.</li>
        </ol>
      </div>

      {/* Embeddable Trust Widget — for website/Google Business */}
      {info?.token && (
        <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-4" data-testid="widget-embed-panel">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="font-bold text-sm text-cyan-200">🌐 Anant Trust Widget — Website / Google Business ke liye</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Ye code apni website ya Google Business post me paste karein — visitor ko live trust badge dikhega + view counter track hoga.</div>
            </div>
            {widgetStats && (
              <div className="text-right shrink-0">
                <div className="text-[9px] uppercase font-bold text-cyan-300">Widget Views</div>
                <div className="text-xl font-extrabold text-cyan-200" data-testid="widget-views-count">{widgetStats.total_views || 0}</div>
                <div className="text-[9px] text-slate-500">{widgetStats.unique_referrers || 0} sites</div>
              </div>
            )}
          </div>
          <button onClick={() => setShowEmbed((v) => !v)} data-testid="widget-embed-toggle" className="w-full py-2 rounded-lg bg-cyan-500 text-white font-bold text-xs">
            {showEmbed ? 'Hide Embed Code' : '📋 Get Embed Code'}
          </button>
          {showEmbed && (() => {
            const embedUrl = `${window.location.origin}/anant-live/widget?shop=${info.token}`;
            const embedCode = `<iframe src="${embedUrl}" width="340" height="260" style="border:0;overflow:hidden" title="Verified by Anant Network"></iframe>`;
            const netEmbed = `<iframe src="${window.location.origin}/anant-live/widget" width="340" height="260" style="border:0" title="Anant Network Live"></iframe>`;
            return (
              <div className="mt-3 space-y-3">
                <div>
                  <div className="text-[10px] uppercase font-bold text-cyan-300 mb-1">🛡️ Your Shop Badge (Recommended)</div>
                  <textarea readOnly value={embedCode} data-testid="widget-embed-shop-code" className="w-full text-[10px] font-mono bg-slate-900 border border-white/10 rounded p-2 h-20 text-cyan-100" onFocus={(e) => e.target.select()} />
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => { navigator.clipboard.writeText(embedCode); toast.success('Embed code copied'); }} data-testid="widget-embed-copy" className="flex-1 py-1.5 rounded bg-cyan-500 text-white text-[10px] font-bold">📋 Copy</button>
                    <a href={embedUrl} target="_blank" rel="noreferrer" data-testid="widget-embed-preview" className="flex-1 py-1.5 rounded bg-white/10 text-[10px] font-bold text-center">👁️ Preview</a>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-fuchsia-300 mb-1">📊 Anant Network Stats Widget</div>
                  <textarea readOnly value={netEmbed} className="w-full text-[10px] font-mono bg-slate-900 border border-white/10 rounded p-2 h-20 text-fuchsia-100" onFocus={(e) => e.target.select()} />
                  <button onClick={() => { navigator.clipboard.writeText(netEmbed); toast.success('Network widget code copied'); }} className="w-full mt-1 py-1.5 rounded bg-fuchsia-500 text-white text-[10px] font-bold">📋 Copy Network Widget</button>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// ============ Setup Tab: Industry Templates + Custom Field Builder ============
const SetupTab = ({ workspace, onRefresh }) => {
  const [templates, setTemplates] = useState([]);
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(null);
  const [newField, setNewField] = useState({ label: '', field_type: 'text', options: '' });

  const loadFields = useCallback(async () => {
    try { const r = await api.get(`/vyapar/custom-fields?workspace_id=${workspace.id}`); setFields(r.data.fields || []); } catch {}
  }, [workspace.id]);
  useEffect(() => {
    loadFields();
    api.get('/vyapar/industry-templates').then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, [loadFields]);

  const applyTemplate = async (tid) => {
    if (!window.confirm('Ye template aapke workspace par quick-bill items + custom fields set karega. Continue?')) return;
    setBusy(tid);
    try {
      const r = await api.post(`/vyapar/workspace/${workspace.id}/apply-template`, { industry_id: tid });
      toast.success(`✅ Template applied! ${r.data.quick_items} quick items · ${r.data.custom_fields_created} custom fields`);
      loadFields(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const addField = async () => {
    if (!newField.label.trim()) return toast.error('Field ka naam daalein');
    const payload = {
      label: newField.label.trim(),
      field_type: newField.field_type,
      options: newField.field_type === 'select' ? newField.options.split(',').map((o) => o.trim()).filter(Boolean) : [],
      applies_to: 'customer',
    };
    try {
      await api.post(`/vyapar/custom-fields?workspace_id=${workspace.id}`, payload);
      toast.success('Custom field added');
      setNewField({ label: '', field_type: 'text', options: '' });
      loadFields();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const delField = async (id) => {
    if (!window.confirm('Delete this field?')) return;
    await api.delete(`/vyapar/custom-fields/${id}`);
    toast.success('Deleted'); loadFields();
  };

  return (
    <div className="space-y-4" data-testid="vyapar-setup-tab">
      {/* Industry templates */}
      <div className="rounded-2xl border border-fuchsia-400/30 bg-slate-900/60 p-4">
        <h3 className="font-bold mb-1 flex items-center gap-1.5"><Package className="w-4 h-4 text-fuchsia-300" /> 12 Industry Deep Templates</h3>
        <p className="text-xs text-slate-400 mb-3">Apni industry chunein — quick-billing items aur ready-made custom fields ek click mein set ho jayenge.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {templates.map((t) => (
            <button key={t.id} onClick={() => applyTemplate(t.id)} disabled={busy === t.id} data-testid={`tpl-apply-${t.id}`}
              className={`text-left rounded-xl border p-3 transition hover:border-fuchsia-400/60 ${workspace.industry === t.id ? 'border-emerald-400/60 bg-emerald-500/10' : 'border-white/10 bg-white/5'}`}>
              <div className="font-bold text-xs">{t.label}</div>
              <div className="text-[10px] text-slate-400 mt-1">{t.items_count} items · {t.fields_count} fields</div>
              {workspace.industry === t.id && <div className="text-[10px] text-emerald-300 mt-1 font-bold">✓ CURRENT</div>}
              {busy === t.id && <div className="text-[10px] text-amber-300 mt-1">Applying…</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Field Builder */}
      <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
        <h3 className="font-bold mb-1 flex items-center gap-1.5"><Edit3 className="w-4 h-4 text-amber-300" /> Custom Field Builder</h3>
        <p className="text-xs text-slate-400 mb-3">Customer form mein apne khud ke fields add karein (max 25). Ye har customer ke record mein save hote hain.</p>
        <div className="flex flex-wrap gap-2 items-end mb-3">
          <div className="flex-1 min-w-[160px]">
            <label className="text-[10px] text-slate-400 uppercase font-bold">Field Name</label>
            <input value={newField.label} onChange={(e) => setNewField((f) => ({ ...f, label: e.target.value }))} data-testid="cf-new-label" placeholder="e.g. Vehicle Number" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase font-bold">Type</label>
            <select value={newField.field_type} onChange={(e) => setNewField((f) => ({ ...f, field_type: e.target.value }))} data-testid="cf-new-type" className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm block">
              <option value="text" className="bg-slate-900">Text</option>
              <option value="number" className="bg-slate-900">Number</option>
              <option value="date" className="bg-slate-900">Date</option>
              <option value="select" className="bg-slate-900">Dropdown</option>
              <option value="url" className="bg-slate-900">🔗 URL</option>
              <option value="link" className="bg-slate-900">🌐 Link</option>
              <option value="image_url" className="bg-slate-900">📷 Image URL</option>
              <option value="timer" className="bg-slate-900">⏳ Timer / Countdown</option>
            </select>
          </div>
          {newField.field_type === 'select' && (
            <div className="flex-1 min-w-[160px]">
              <label className="text-[10px] text-slate-400 uppercase font-bold">Options (comma separated)</label>
              <input value={newField.options} onChange={(e) => setNewField((f) => ({ ...f, options: e.target.value }))} data-testid="cf-new-options" placeholder="Option1, Option2" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
          <button onClick={addField} data-testid="cf-add-btn" className="px-3 py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Add Field</button>
        </div>
        <div className="space-y-1.5">
          {fields.length === 0 && <div className="text-xs text-slate-500">Abhi koi custom field nahi. Upar se add karein ya industry template apply karein.</div>}
          {fields.map((f) => (
            <div key={f.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 text-sm" data-testid={`cf-row-${f.id}`}>
              <div>
                <b>{f.label}</b>
                <span className="text-[10px] text-slate-400 ml-2 uppercase">{f.field_type}</span>
                {f.field_type === 'select' && <span className="text-[10px] text-slate-500 ml-2">[{(f.options || []).join(' · ')}]</span>}
                {f.from_template && <span className="text-[9px] text-fuchsia-300 ml-2">via template</span>}
              </div>
              <button onClick={() => delField(f.id)} data-testid={`cf-del-${f.id}`} className="p-1 rounded hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Quick items preview */}
      {(workspace.quick_items || []).length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
          <h3 className="font-bold mb-2 text-sm">⚡ Quick Billing Items ({workspace.quick_items.length})</h3>
          <div className="flex flex-wrap gap-1.5">
            {workspace.quick_items.map((it, i) => (
              <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-400/30">{it.name} · ₹{it.rate}</span>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Ye items "Naya Bill" banate waqt one-click add ho sakte hain.</p>
        </div>
      )}
    </div>
  );
};

// ============ Plans Tab ============
const PlansTab = ({ plan, onPurchased }) => {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get('/vyapar/plans').then((r) => setPlans(r.data.plans || [])); }, []);
  const buy = async (planId) => {
    if (!window.confirm('Are you sure? AT wallet se deduct hoga.')) return;
    setBusy(planId);
    try {
      const r = await api.post('/vyapar/plans/purchase', { plan_id: planId });
      toast.success(`✅ Plan activated! Expires ${new Date(r.data.subscription.expires_at).toLocaleDateString()}${r.data.subscription.cashback_at ? ` · Cashback: ${r.data.subscription.cashback_at} AT 🎉` : ''}`);
      onPurchased();
    } catch (e) { toast.error(e.response?.data?.detail || 'Purchase failed'); }
    finally { setBusy(null); }
  };
  const standard = plans.filter((p) => p.kind === 'standard');
  const vip = plans.filter((p) => p.kind === 'vip');
  return (
    <div className="space-y-4" data-testid="vyapar-plans-tab">
      <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-xs text-amber-100">
        💰 <b>Early Renewal Cashback:</b> Expiry se 3+ din pehle renew karein = 50 AT cashback. Yearly plan par 7+ din pehle = 100 AT cashback! 🎁
      </div>
      <div>
        <h3 className="font-bold mb-2 flex items-center gap-1"><Sparkles className="w-4 h-4 text-fuchsia-300" /> Standard Plans (1 Business)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {standard.map((p) => <PlanCard key={p.id} plan={p} current={plan?.plan_id === p.id} busy={busy === p.id} onBuy={() => buy(p.id)} />)}
        </div>
      </div>
      <div>
        <h3 className="font-bold mb-2 flex items-center gap-1"><Crown className="w-4 h-4 text-amber-300" /> VIP Plans (3 Businesses)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {vip.map((p) => <PlanCard key={p.id} plan={p} current={plan?.plan_id === p.id} busy={busy === p.id} onBuy={() => buy(p.id)} />)}
        </div>
      </div>
    </div>
  );
};

const PlanCard = ({ plan, current, busy, onBuy }) => (
  <div className={`rounded-2xl border p-4 relative ${plan.dfy ? 'border-emerald-400/50 bg-emerald-500/10' : 'border-white/10 bg-slate-900/50'}`} data-testid={`plan-card-${plan.id}`}>
    {plan.dfy && <div className="absolute -top-2 left-2 text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold">DFY FREE</div>}
    {current && <div className="absolute -top-2 right-2 text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold">CURRENT</div>}
    <div className="text-sm font-bold">{plan.name}</div>
    <div className="text-3xl font-extrabold mt-2">{plan.price_at} <span className="text-xs text-slate-400 font-normal">AT</span></div>
    <div className="text-xs text-slate-400 mt-1">{plan.duration_days} days</div>
    {plan.dfy && <div className="text-[11px] text-emerald-300 mt-2">✓ Free 500 customers data entry by our team</div>}
    <button onClick={onBuy} disabled={busy || current} data-testid={`plan-buy-${plan.id}`} className="w-full mt-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-bold text-xs disabled:opacity-50">
      {current ? 'Active' : busy ? 'Processing…' : 'Buy Plan'}
    </button>
  </div>
);

// ============ Categories / Sub-categories Tab ============
const CategoriesTab = ({ workspace }) => {
  const { t } = useVyaparI18n();
  const [tree, setTree] = useState({ categories: [], orphans: [], total: 0 });
  const [form, setForm] = useState({ name: '', parent_id: '', description: '', image_url: '', sort_order: 100 });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/vyapar/categories?workspace_id=${workspace.id}`);
      setTree(r.data);
    } catch { /* ignore */ }
  }, [workspace.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error(t('name') + ' ' + 'required');
    setBusy(true);
    try {
      const payload = { ...form, workspace_id: workspace.id, parent_id: form.parent_id || null };
      if (editing) {
        await api.patch(`/vyapar/categories/${editing}`, payload);
        toast.success('✅ Updated');
      } else {
        await api.post('/vyapar/categories', payload);
        toast.success('✅ ' + t('category') + ' added');
      }
      setForm({ name: '', parent_id: '', description: '', image_url: '', sort_order: 100 });
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const edit = (c) => { setEditing(c.id); setForm({ name: c.name, parent_id: c.parent_id || '', description: c.description || '', image_url: c.image_url || '', sort_order: c.sort_order || 100 }); };
  const del = async (id) => {
    if (!window.confirm(t('delete') + '? (Sub-categories bhi delete honge)')) return;
    try { await api.delete(`/vyapar/categories/${id}?workspace_id=${workspace.id}`); toast.success(t('delete') + ' ✓'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const allParents = tree.categories;

  return (
    <div className="space-y-3" data-testid="vyapar-categories-tab">
      <div className="rounded-2xl border border-indigo-400/30 bg-indigo-500/5 p-4">
        <div className="text-sm font-bold text-indigo-300 mb-2">📂 {t('manage_categories')}</div>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
          <input placeholder={t('name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" className="sm:col-span-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="cat-parent" className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm">
            <option value="">— {t('category')} (top level) —</option>
            {allParents.map((p) => <option key={p.id} value={p.id} className="bg-slate-900">↳ {t('subcategory')} of {p.name}</option>)}
          </select>
          <input placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          <button onClick={submit} disabled={busy} data-testid="cat-save" className="py-1.5 rounded-lg bg-indigo-500 text-white text-sm font-bold disabled:opacity-60">{busy ? '…' : (editing ? t('save') : t('add'))}</button>
        </div>
        {editing && <button onClick={() => { setEditing(null); setForm({ name: '', parent_id: '', description: '', image_url: '', sort_order: 100 }); }} className="text-xs text-slate-400 mt-2">{t('cancel')} edit</button>}
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm font-bold mb-3">{t('category')} · {tree.total}</div>
        {tree.categories.length === 0 && <div className="text-center py-6 text-slate-500 text-xs">Koi {t('category')} nahi. Upar se add karein.</div>}
        <div className="space-y-2">
          {tree.categories.map((c) => (
            <div key={c.id} className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {c.image_url && <img src={c.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                  <div>
                    <div className="text-sm font-bold">{c.name}</div>
                    {c.description && <div className="text-[10px] text-slate-400">{c.description}</div>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => edit(c)} data-testid={`cat-edit-${c.id}`} className="text-[10px] px-2 py-1 rounded bg-indigo-500/20 text-indigo-200 font-bold">{t('edit')}</button>
                  <button onClick={() => del(c.id)} data-testid={`cat-del-${c.id}`} className="p-1 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {c.children && c.children.length > 0 && (
                <div className="mt-2 pl-4 border-l-2 border-indigo-400/30 space-y-1">
                  {c.children.map((sc) => (
                    <div key={sc.id} className="flex items-center justify-between text-xs bg-white/5 rounded px-2 py-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-indigo-300">↳</span>
                        {sc.image_url && <img src={sc.image_url} alt="" className="w-5 h-5 rounded object-cover" />}
                        <span>{sc.name}</span>
                        {sc.description && <span className="text-[10px] text-slate-500 truncate">· {sc.description}</span>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => edit(sc)} className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-200">{t('edit')}</button>
                        <button onClick={() => del(sc.id)} className="p-0.5 text-red-300"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ Inventory / Stock Tab ============
const InventoryTab = ({ workspace }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [low, setLow] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [adjItem, setAdjItem] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/vyapar/inventory?workspace_id=${workspace.id}`);
      setItems(r.data.items || []); setTotal(r.data.total_value || 0); setLow(r.data.low_stock_count || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load stock'); }
  }, [workspace.id]);
  useEffect(() => { load(); }, [load]);
  const filtered = items.filter((i) => !q || i.name.toLowerCase().includes(q.toLowerCase()) || (i.sku || '').toLowerCase().includes(q.toLowerCase()));
  const del = async (item) => { if (!window.confirm(`Delete ${item.name}?`)) return; await api.delete(`/vyapar/inventory/${item.id}`); toast.success('Deleted'); load(); };
  return (
    <div className="space-y-3" data-testid="vyapar-inventory-tab">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 p-3"><div className="text-[10px] uppercase text-fuchsia-300">Total Items</div><div className="text-2xl font-extrabold">{items.length}</div></div>
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3"><div className="text-[10px] uppercase text-emerald-300">Stock Value</div><div className="text-2xl font-extrabold">₹{total}</div></div>
        <div className={`rounded-xl border p-3 ${low > 0 ? 'border-red-400/40 bg-red-500/10' : 'border-white/10 bg-slate-800/40'}`}><div className={`text-[10px] uppercase ${low > 0 ? 'text-red-300' : 'text-slate-400'}`}>Low Stock ⚠️</div><div className="text-2xl font-extrabold">{low}</div></div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item ya SKU" data-testid="stock-search" className="flex-1 min-w-[160px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <button onClick={() => setShowAdd(true)} data-testid="stock-add-btn" className="px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Add Item</button>
      </div>
      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">SKU</th><th className="text-right px-3 py-2">Rate</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Value</th><th className="px-3 py-2">Actions</th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs">Koi item nahi. "Add Item" par click karein.</td></tr>}
            {filtered.map((i) => {
              const low = (i.qty_in_stock || 0) <= (i.low_stock_alert || 0);
              return (
                <tr key={i.id} className={`border-t border-white/5 hover:bg-white/5 ${low ? 'bg-red-500/5' : ''}`}>
                  <td className="px-3 py-2 font-bold">{low && '⚠️ '}{i.name}</td>
                  <td className="px-3 py-2 text-xs text-slate-400">{i.sku || '—'}</td>
                  <td className="px-3 py-2 text-right">₹{i.rate}</td>
                  <td className={`px-3 py-2 text-right font-bold ${low ? 'text-red-300' : 'text-emerald-300'}`}>{i.qty_in_stock} <span className="text-[10px] text-slate-500">{i.unit}</span></td>
                  <td className="px-3 py-2 text-right text-slate-300">₹{(i.rate * i.qty_in_stock).toFixed(0)}</td>
                  <td className="px-3 py-2 flex gap-1 justify-center">
                    <button onClick={() => setAdjItem(i)} data-testid={`stock-adj-${i.id}`} className="px-2 py-1 rounded bg-fuchsia-500/20 text-fuchsia-300 text-[10px] font-bold">Adjust</button>
                    <button onClick={() => del(i)} data-testid={`stock-del-${i.id}`} className="p-1 rounded hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showAdd && <StockAddModal workspace={workspace} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {adjItem && <StockAdjustModal item={adjItem} onClose={() => setAdjItem(null)} onSaved={() => { setAdjItem(null); load(); }} />}
    </div>
  );
};

const StockAddModal = ({ workspace, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: '', sku: '', unit: 'pcs', rate: '', qty_in_stock: '', low_stock_alert: 5 });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name) return toast.error('Item ka naam daalein');
    setBusy(true);
    try {
      await api.post(`/vyapar/inventory?workspace_id=${workspace.id}`, {
        ...form, rate: Number(form.rate || 0), qty_in_stock: Number(form.qty_in_stock || 0), low_stock_alert: Number(form.low_stock_alert || 5),
      });
      toast.success('Item added'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="stock-add-modal">
        <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-bold">➕ Add Stock Item</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <input placeholder="Item name *" value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="stock-form-name" className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="SKU / Code" value={form.sku} onChange={(e) => set('sku', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <select value={form.unit} onChange={(e) => set('unit', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            {['pcs','kg','ltr','box','pack','meter','dozen'].map((u) => <option key={u} value={u} className="bg-slate-900">{u}</option>)}
          </select>
          <input type="number" placeholder="Rate (₹)" value={form.rate} onChange={(e) => set('rate', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input type="number" placeholder="Qty in stock" value={form.qty_in_stock} onChange={(e) => set('qty_in_stock', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input type="number" placeholder="Low stock alert (default 5)" value={form.low_stock_alert} onChange={(e) => set('low_stock_alert', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
        </div>
        <button onClick={submit} disabled={busy} data-testid="stock-form-save" className="w-full mt-3 py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm disabled:opacity-60">{busy ? 'Saving…' : 'Add Item'}</button>
      </div>
    </div>
  );
};

const StockAdjustModal = ({ item, onClose, onSaved }) => {
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('purchase');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (sign) => {
    const d = Number(delta || 0);
    if (!d) return toast.error('Qty daalein');
    setBusy(true);
    try {
      await api.post(`/vyapar/inventory/${item.id}/adjust`, { delta: sign * Math.abs(d), reason, note });
      toast.success('Stock updated'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="stock-adj-modal">
        <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-bold">📦 {item.name}</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        <div className="text-xs text-slate-400 mb-3">Current stock: <b className="text-emerald-300">{item.qty_in_stock} {item.unit}</b></div>
        <input type="number" placeholder="Qty" value={delta} onChange={(e) => setDelta(e.target.value)} data-testid="stock-adj-delta" className="w-full mb-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full mb-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
          <option value="purchase" className="bg-slate-900">🟢 Purchase (Naya stock)</option>
          <option value="sale" className="bg-slate-900">🔴 Sale (Bik gaya)</option>
          <option value="return" className="bg-slate-900">↩️ Return</option>
          <option value="damage" className="bg-slate-900">💥 Damaged</option>
          <option value="adjustment" className="bg-slate-900">⚙️ Adjustment</option>
        </select>
        <input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="w-full mb-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <div className="flex gap-2">
          <button onClick={() => submit(1)} disabled={busy} data-testid="stock-adj-in" className="flex-1 py-2 rounded-lg bg-emerald-500 text-white font-bold text-xs">➕ Stock In</button>
          <button onClick={() => submit(-1)} disabled={busy} data-testid="stock-adj-out" className="flex-1 py-2 rounded-lg bg-red-500 text-white font-bold text-xs">➖ Stock Out</button>
        </div>
      </div>
    </div>
  );
};

